import React, { useState, useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { auth, firestore, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import PublicRecordCorpusTotals, { normalizeCorpusTotals } from '../components/PublicRecordCorpusTotals';
import HarnettAcquisitionPanel from '../components/HarnettAcquisitionPanel';
import PublicRecordComplianceRemoval from './PublicRecordComplianceRemoval';
import {
  collection,
  query,
  orderBy,
  deleteDoc,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import './AdminDashboard.css';

const SUMMARY_CACHE_KEY = 'bluewire_admin_stats_cache';
const HISTORICAL_SOURCES = [
  { id: 'nc_mecklenburg_sheriff', label: 'Mecklenburg County', duplicateReviewEnabled: false },
  { id: 'nc_wake_bfs', label: 'Wake County', duplicateReviewEnabled: true },
];
const HARNETT_SOURCE_ID = 'nc_harnett_sheriff';
const ACQUISITION_SOURCES = [
  ...HISTORICAL_SOURCES,
  { id: HARNETT_SOURCE_ID, label: 'Harnett County', duplicateReviewEnabled: false },
];
const EMPTY_CORPUS_TOTALS = Object.freeze({
  sourceRecords: 0, resolvedSourceIdentities: 0, retainedPhotos: 0, indexedFaces: 0,
});
const SKIP_REASON_LABELS = Object.freeze({
  CARD_REPORT_NAME_MISMATCH: 'Card/report name mismatch',
  CARD_SCHEMA_INVALID: 'Card schema invalid',
  REPORT_SCHEMA_INVALID: 'Report schema invalid',
  SOURCE_PHOTO_UNAVAILABLE: 'Source photo unavailable',
  SOURCE_PHOTO_NOT_FOUND: 'Source photo not found',
  SOURCE_PHOTO_INVALID_IMAGE: 'Source photo unusable',
  SOURCE_PHOTO_INVALID_RESPONSE: 'Invalid photo response',
});
const skipReasonLabel = (code) => SKIP_REASON_LABELS[code] || code.replaceAll('_', ' ').toLowerCase();
const CURRENT_ERROR_STATUSES = new Set(['RETRY_BACKOFF', 'ATTENTION_REQUIRED', 'FAILED']);
const ACTIVE_ACQUISITION_STATUSES = new Set(['STARTING', 'RUNNING', 'RETRY_BACKOFF', 'PAUSE_REQUESTED']);
const ACTIVE_DUPLICATE_BATCH_STATUSES = new Set(['PREPARING', 'RUNNING', 'WAITING_RETRY']);
const DUPLICATE_BATCH_REASON_LABELS = Object.freeze({
  INITIAL_MIDDLE: 'Middle-name initial only',
  MISSING_FIRST: 'Missing first name',
  MISSING_LAST: 'Missing last name',
  MISSING_MIDDLE: 'Missing middle name',
  NAME_CONFLICT: 'Name conflict',
  TRUNCATED_MIDDLE: 'Truncated middle name',
  UNCERTAIN: 'Uncertain or unavailable comparison',
});
const callableErrorCode = (error) => String(error?.code || '').replace(/^functions\//, '');
const publicRecordErrorMessage = (error, fallback) => {
  const code = callableErrorCode(error);
  if (code === 'failed-precondition' && /active historical job|already running/i.test(error?.message || '')) {
    return 'An acquisition is already running. Status has been refreshed.';
  }
  if (code === 'unauthenticated') return 'Your admin session has expired. Sign in again and refresh status.';
  if (code === 'permission-denied') return 'This account is not authorized to control Public Records.';
  if (['unavailable', 'network-request-failed'].includes(code)) {
    return 'Public Records status is temporarily unavailable. Last-known values may be stale.';
  }
  if (code === 'internal') return 'The Public Records service returned an internal error. Refresh status before retrying.';
  return error?.details?.publicRecordCode || error?.message || fallback;
};
const hasCurrentHistoricalError = (job) => CURRENT_ERROR_STATUSES.has(job?.status) &&
  Boolean(job?.lastErrorCode || job?.lastErrorMessage);
const currentErrorPresentationClass = (job) => job?.status === 'RETRY_BACKOFF' ?
  'ingestion-warning' : 'ingestion-error';
const safePendingReviewCount = (value) => Number.isSafeInteger(value) && value >= 0 ? value : 0;
const cleanupCount = (value) => Number.isSafeInteger(value) && value >= 0 ? value : 0;
const cleanupWorkerStatus = (status) => status?.job?.status || 'NOT_STARTED';
const cleanupHasActionableWork = (status) => cleanupCount(status?.requested) > 0 ||
  cleanupCount(status?.processing) > 0 || cleanupCount(status?.retryable) > 0 ||
  cleanupCount(status?.deferred) > 0 || cleanupCount(status?.quarantined) > 0 ||
  cleanupCount(status?.duplicateState?.total) > 0 || Boolean(status?.job?.lastErrorCode);
const cleanupIsActive = (status) => ['RUNNING', 'BLOCKED_REVIEW', 'BLOCKED_ACQUISITION', 'WAITING_RETRY']
  .includes(cleanupWorkerStatus(status)) || cleanupCount(status?.requested) > 0 ||
  cleanupCount(status?.processing) > 0;
const cleanupNeedsDetails = (status) => cleanupIsActive(status) || cleanupHasActionableWork(status) ||
  cleanupWorkerStatus(status) === 'PAUSED';
const cleanupStatusMessage = (status) => {
  if (!status) return 'No marked cleanup work is currently pending.';
  const worker = cleanupWorkerStatus(status);
  const retryable = cleanupCount(status.retryable);
  const deferred = cleanupCount(status.deferred);
  const quarantined = cleanupCount(status.quarantined);
  if (quarantined || retryable || status?.job?.lastErrorCode) {
    return `Marked cleanup needs attention — ${retryable} retriable · ${quarantined} quarantined.`;
  }
  if (deferred || cleanupCount(status?.duplicateState?.total)) {
    return deferred ? `Marked cleanup is waiting on ${deferred} deferred target${deferred === 1 ? '' : 's'}.` :
      'Marked cleanup is waiting for Wake Duplicate Reviews to finish.';
  }
  if (cleanupIsActive(status)) return 'Marked cleanup is running in bounded background batches.';
  if (worker === 'COMPLETED') {
    return `Marked cleanup complete — ${cleanupCount(status.currentRunCompleted)} processed this run.`;
  }
  return 'No marked cleanup work is currently pending.';
};
const gapScopeLabel = (scope) => ({
  card: 'Card',
  page: 'Page',
  partition_remainder: 'Partition remainder',
}[scope] || 'Unknown');
const gapScopeMeaning = (scope) => ({
  card: 'One record was skipped.',
  page: 'This page is unresolved.',
  partition_remainder: 'Coverage after this point is unresolved.',
}[scope] || '');
const normalizeAcquisitionGaps = (value, sourceId) => Array.isArray(value) ? value.filter((gap) =>
  gap && gap.sourceId === sourceId && typeof gap.partition === 'string' &&
  ['card', 'page', 'partition_remainder'].includes(gap.scope) &&
  ['unresolved', 'retrying'].includes(gap.status)) : [];
const wakeRetryMessage = (job) => {
  if (!hasCurrentHistoricalError(job) || job.status !== 'RETRY_BACKOFF' ||
    job.sourceId !== 'nc_wake_bfs' || job.lastErrorCode !== 'wake-request-timeout') return '';
  const hasRetryTimestamp = typeof job.nextAttemptAt === 'string' &&
    !Number.isNaN(new Date(job.nextAttemptAt).getTime());
  return hasRetryTimestamp ?
    `Wake County request timed out. Automatic retry scheduled for ${formatHistoricalTimestamp(job.nextAttemptAt)}.` :
    'Wake County request timed out. Automatic retry is scheduled.';
};
export const formatHistoricalTimestamp = (value) => {
  if (!value || typeof value !== 'string') return 'N/A';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return 'N/A';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(timestamp);
  } catch (_) {
    return 'N/A';
  }
};

const sourceLocalDateKey = (now = new Date()) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    day: '2-digit', month: '2-digit', timeZone: 'America/New_York', year: 'numeric',
  }).formatToParts(now).filter((part) => part.type !== 'literal')
    .map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};
export const historicalDateRangeError = (start, end, today = sourceLocalDateKey()) => {
  const explicitDate = /^[1-9]\d{3}-\d{2}-\d{2}$/;
  const validDate = (value) => {
    if (!explicitDate.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  };
  if (!validDate(start) || !validDate(end)) {
    return 'Use valid dates with an explicit four-digit year, for example 2023-08-12.';
  }
  if (start > end) return 'The historical start date must not be after the end date.';
  if (end > today) return 'The historical end date cannot be in the future.';
  return '';
};

const CALENDAR_MONTHS = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]);
const calendarDateKey = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
const calendarDaysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
const calendarFirstWeekday = (year, month) => new Date(Date.UTC(year, month, 1)).getUTCDay();
const currentCalendarYear = () => new Date().getFullYear();
const DUPLICATE_REVIEW_PAGE_LIMIT = 12;
const DUPLICATE_REVIEW_LOW_WATERMARK = 5;
const NAME_REVIEW_PAGE_LIMIT = 20;

const AdminDashboard = () => {
  const [selectedTab, setSelectedTab] = useState('summary');
  const [summaryData, setSummaryData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [rebuildingStats, setRebuildingStats] = useState(false);
  const [rebuildingAgencies, setRebuildingAgencies] = useState(false);
  const [rebuildingSearchLookups, setRebuildingSearchLookups] = useState(false);
  const [agencyMigrationSummary, setAgencyMigrationSummary] = useState(null);
  const [searchLookupSummary, setSearchLookupSummary] = useState(null);
  const [newsFetchBusy, setNewsFetchBusy] = useState('');
  const [newsFetchMessage, setNewsFetchMessage] = useState('');
  const [historicalJob, setHistoricalJob] = useState(null);
  const [historicalSourceId, setHistoricalSourceId] = useState(HISTORICAL_SOURCES[0].id);
  const [historicalCorpusTotals, setHistoricalCorpusTotals] = useState(EMPTY_CORPUS_TOTALS);
  const [historicalGaps, setHistoricalGaps] = useState([]);
  const [historicalBusy, setHistoricalBusy] = useState(false);
  const [historicalMessage, setHistoricalMessage] = useState('');
  const [historicalStatusConfirmed, setHistoricalStatusConfirmed] = useState(false);
  const [historicalDashboardNeedsInitialization, setHistoricalDashboardNeedsInitialization] = useState(false);
  const [historicalDashboardInitializeBusy, setHistoricalDashboardInitializeBusy] = useState(false);
  const [historicalDashboardInitializeError, setHistoricalDashboardInitializeError] = useState('');
  const [acquisitionCalendarOpen, setAcquisitionCalendarOpen] = useState(false);
  const [acquisitionCalendarYear, setAcquisitionCalendarYear] = useState(currentCalendarYear);
  const [acquisitionCalendarDates, setAcquisitionCalendarDates] = useState([]);
  const [acquisitionCalendarBusy, setAcquisitionCalendarBusy] = useState(false);
  const [acquisitionCalendarError, setAcquisitionCalendarError] = useState('');
  const [newAcquisitionOpen, setNewAcquisitionOpen] = useState(false);
  const [skipViewer, setSkipViewer] = useState(null);
  const [skipViewerBusy, setSkipViewerBusy] = useState(false);
  const [skipViewerError, setSkipViewerError] = useState('');
  const [recordReviews, setRecordReviews] = useState([]);
  const [recordReviewBusy, setRecordReviewBusy] = useState(false);
  const [recordReviewError, setRecordReviewError] = useState('');
  const [recordReviewSelections, setRecordReviewSelections] = useState({});
  const [recordReviewEditing, setRecordReviewEditing] = useState({});
  const [recordReviewInFlight, setRecordReviewInFlight] = useState({});
  const [recordReviewFailures, setRecordReviewFailures] = useState({});
  const [observationCleanupStatus, setObservationCleanupStatus] = useState(null);
  const [observationCleanupBusy, setObservationCleanupBusy] = useState('');
  const [observationCleanupMessage, setObservationCleanupMessage] = useState('');
  const [observationCleanupDetailsOpen, setObservationCleanupDetailsOpen] = useState(false);
  const [duplicateBatchSelection, setDuplicateBatchSelection] = useState('100');
  const [duplicateBatchPreview, setDuplicateBatchPreview] = useState(null);
  const [duplicateBatchStatus, setDuplicateBatchStatus] = useState(null);
  const [duplicateBatchBusy, setDuplicateBatchBusy] = useState('');
  const [duplicateBatchError, setDuplicateBatchError] = useState('');
  const [duplicateBatchConfirmOpen, setDuplicateBatchConfirmOpen] = useState(false);
  const [reviewQueueCounts, setReviewQueueCounts] = useState({nameReviewCount: 0,
    duplicateReviewCount: 0, pendingReviewCount: 0,
    duplicateReviewState: {pending: 0, processing: 0, processingFailed: 0}});
  const [reviewQueueView, setReviewQueueView] = useState('name');
  const [reviewSourceFilter, setReviewSourceFilter] = useState('');
  const [reviewWorkspaceOpen, setReviewWorkspaceOpen] = useState(false);
  const [nameReviewNextCursor, setNameReviewNextCursor] = useState(null);
  const [nameReviewLoadMoreBusy, setNameReviewLoadMoreBusy] = useState(false);
  const [publicRecordFaceCatchUp, setPublicRecordFaceCatchUp] = useState(null);
  const [publicRecordFaceCatchUpBusy, setPublicRecordFaceCatchUpBusy] = useState('');
  const [publicRecordFaceCatchUpError, setPublicRecordFaceCatchUpError] = useState('');
  const [publicRecordFaceStatusConfirmed, setPublicRecordFaceStatusConfirmed] = useState(false);
  const [publicRecordFaceCatchUpStartConfirmOpen, setPublicRecordFaceCatchUpStartConfirmOpen] = useState(false);
  const [historicalForm, setHistoricalForm] = useState({
    historicalStartDate: '', historicalEndDate: '', processingBatchSize: '20',
    delayBetweenBatchesMs: '60000', maxConcurrentPhotos: '1',
  });
  const historicalStatusRequest = useRef(0);
  const historicalCreatePending = useRef(false);
  const publicRecordsRefreshInFlight = useRef(false);
  const publicRecordsRefreshPending = useRef(false);
  const duplicateReviewRefillInFlight = useRef(false);
  const duplicateReviewRequestGeneration = useRef(0);
  const duplicateReviewExcludedIds = useRef(new Set());
  const duplicateReviewSourceRef = useRef('');
  const nameReviewRequestGeneration = useRef(0);
  const nameReviewLoadMoreInFlight = useRef(false);
  const observationCleanupWasActionable = useRef(false);
  const historicalSourceIdRef = useRef(historicalSourceId);
  const acquisitionCalendarRequest = useRef(0);
  historicalSourceIdRef.current = historicalSourceId;
  const harnettSourceSelected = historicalSourceId === HARNETT_SOURCE_ID;

  const selectAcquisitionSource = (sourceId) => {
    historicalStatusRequest.current += 1;
    acquisitionCalendarRequest.current += 1;
    duplicateReviewRequestGeneration.current += 1;
    nameReviewRequestGeneration.current += 1;
    setHistoricalSourceId(sourceId);
    setHistoricalJob(null);
    setHistoricalCorpusTotals(EMPTY_CORPUS_TOTALS);
    setHistoricalGaps([]);
    setHistoricalStatusConfirmed(false);
    setHistoricalDashboardNeedsInitialization(false);
    setHistoricalDashboardInitializeError('');
    setHistoricalMessage('');
    setPublicRecordFaceCatchUp(null);
    setPublicRecordFaceStatusConfirmed(false);
    setPublicRecordFaceCatchUpError('');
    setPublicRecordFaceCatchUpStartConfirmOpen(false);
    setReviewQueueCounts({nameReviewCount: 0, duplicateReviewCount: 0, pendingReviewCount: 0,
      duplicateReviewState: {pending: 0, processing: 0, processingFailed: 0}});
    setReviewWorkspaceOpen(false);
    setReviewSourceFilter('');
    setRecordReviews([]);
    setRecordReviewError('');
    setNameReviewNextCursor(null);
    setSkipViewer(null);
    setAcquisitionCalendarOpen(false);
    setObservationCleanupDetailsOpen(false);
    setDuplicateBatchPreview(null);
    setDuplicateBatchStatus(null);
    setDuplicateBatchBusy('');
    setDuplicateBatchError('');
    setDuplicateBatchConfirmOpen(false);
  };

  const publicRecordFaceControl = () => httpsCallable(functions, 'publicRecordFaceControl');
  const refreshPublicRecordFaceCatchUp = async ({manageBusy = true} = {}) => {
    const requestedSourceId = historicalSourceIdRef.current;
    if (requestedSourceId === HARNETT_SOURCE_ID) return false;
    if (manageBusy) setPublicRecordFaceCatchUpBusy('refresh');
    setPublicRecordFaceCatchUpError('');
    try {
      const result = await publicRecordFaceControl()({action: 'catch_up_status',
        sourceId: requestedSourceId});
      if (requestedSourceId !== historicalSourceIdRef.current) return false;
      setPublicRecordFaceCatchUp(result?.data || null);
      setPublicRecordFaceStatusConfirmed(typeof result?.data?.status === 'string');
      return true;
    } catch (error) {
      if (requestedSourceId !== historicalSourceIdRef.current) return false;
      setPublicRecordFaceStatusConfirmed(false);
      setPublicRecordFaceCatchUpError(publicRecordErrorMessage(error,
          'Could not load catch-up status.'));
      return false;
    } finally {
      if (manageBusy) setPublicRecordFaceCatchUpBusy('');
    }
  };
  const controlPublicRecordFaceCatchUp = async (action, busy) => {
    const requestedSourceId = historicalSourceIdRef.current;
    setPublicRecordFaceCatchUpBusy(busy);
    setPublicRecordFaceCatchUpError('');
    try {
      const result = await publicRecordFaceControl()({action,
        sourceId: requestedSourceId});
      if (requestedSourceId !== historicalSourceIdRef.current) return;
      setPublicRecordFaceCatchUp(result?.data || null);
      setPublicRecordFaceStatusConfirmed(typeof result?.data?.status === 'string');
      await refreshPublicRecordStatus({manageBusy: false});
    } catch (error) {
      setPublicRecordFaceStatusConfirmed(false);
      setPublicRecordFaceCatchUpError(publicRecordErrorMessage(error,
          'Could not update face-index catch-up.'));
    } finally {
      setPublicRecordFaceCatchUpBusy('');
    }
  };
  const confirmPublicRecordFaceCatchUpStart = async () => {
    setPublicRecordFaceCatchUpStartConfirmOpen(false);
    await controlPublicRecordFaceCatchUp('catch_up_start', 'start');
  };
  // ID Review tab state
  const [idReviewCases, setIdReviewCases] = useState([]);
  const [idReviewLoading, setIdReviewLoading] = useState(false);
  const [idReviewError, setIdReviewError] = useState('');
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [reviewActionLoading, setReviewActionLoading] = useState(null); // userId being actioned
  const getVerificationImages = httpsCallable(functions, 'getVerificationImages');

  const fetchSummaryData = async () => {
    try {
      const statsRef = doc(firestore, 'admin', 'masterStats');
      const snap = await getDoc(statsRef);

      if (snap.exists()) {
        const data = snap.data();
        setSummaryData(data);
        localStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify(data));
      } else {
        console.warn('masterStats doc missing');
      }
    } catch (err) {
      console.error('Error fetching summary data:', err);
    }
  };

  const handleRebuildMasterStats = async () => {
    const confirmed = window.confirm(
        'Are you sure you want to trigger a full rebuild of the masterStats in Firestore?',
    );
    if (!confirmed) return;

    try {
      setRebuildingStats(true);
      const rebuild = httpsCallable(functions, 'rebuildMasterStats');
      await rebuild();
      await fetchSummaryData();
    } catch (err) {
      console.error('Error rebuilding master stats:', err);
    } finally {
      setRebuildingStats(false);
    }
  };

  const handleRebuildAgenciesByState = async () => {
    const confirmed = window.confirm(
        'Are you sure you want to rebuild state agency lookup docs from all existing user profiles?',
    );
    if (!confirmed) return;

    try {
      setRebuildingAgencies(true);
      const rebuild = httpsCallable(functions, 'rebuildAgenciesByState');
      const result = await rebuild();
      setAgencyMigrationSummary(result?.data || null);
    } catch (err) {
      console.error('Error rebuilding agencies by state:', err);
    } finally {
      setRebuildingAgencies(false);
    }
  };

  const handleRebuildProfileSearchLookups = async () => {
    const confirmed = window.confirm(
        'Are you sure you want to rebuild state search lookup docs from all existing user profiles?',
    );
    if (!confirmed) return;

    try {
      setRebuildingSearchLookups(true);
      const rebuild = httpsCallable(functions, 'rebuildProfileSearchLookups');
      const result = await rebuild();
      setSearchLookupSummary(result?.data || null);
    } catch (err) {
      console.error('Error rebuilding profile search lookups:', err);
    } finally {
      setRebuildingSearchLookups(false);
    }
  };

  const handleRunNewsFetch = async (feedType) => {
    const label = feedType === 'local' ? 'local' : 'national';
    const confirmed = window.confirm(
        `Run the ${label} news fetch now? This may take several minutes.`,
    );
    if (!confirmed) return;

    try {
      setNewsFetchBusy(label);
      setNewsFetchMessage('');
      const callableName =
        feedType === 'local' ? 'runLocalNewsPipelineNow' : 'runNewsPipelineNow';
      const runFetch = httpsCallable(functions, callableName);
      await runFetch();
      setNewsFetchMessage(
          `${label.charAt(0).toUpperCase() + label.slice(1)} news fetch completed.`,
      );
    } catch (err) {
      console.error(`Error running ${label} news fetch:`, err);
      setNewsFetchMessage(
          err?.message || `Could not run the ${label} news fetch.`,
      );
    } finally {
      setNewsFetchBusy('');
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem(SUMMARY_CACHE_KEY);
    if (cached) {
      try {
        setSummaryData(JSON.parse(cached));
      } catch (e) {
        console.error('Failed parsing cached summary:', e);
      }
    }

    const fetchMessages = async () => {
      const q = query(
        collection(firestore, 'contactMessages'),
        orderBy('timestamp', 'desc'),
      );
      const snapshot = await getDocs(q);

      const data = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const msgData = docSnap.data();
          const repliesSnap = await getDocs(
            collection(docSnap.ref, 'replies'),
          );
          const replies = repliesSnap.docs.map((replyDoc) => ({
            id: replyDoc.id,
            ...replyDoc.data(),
          }));
          return {
            id: docSnap.id,
            ...msgData,
            replies,
          };
        }),
      );

      setMessages(data);
    };

    fetchMessages();
  }, []);

  // ── ID Review: fetch all "review" verifications ────────────────────────────
  const fetchIdReviewCases = async () => {
    setIdReviewLoading(true);
    setIdReviewError('');
    try {
      console.log('[AdminDashboard] Loading ID review cases...');
      const q = query(
        collection(firestore, 'verifications'),
        where('verificationStatus', '==', 'review'),
        orderBy('updatedAt', 'desc')
      );
      const snap = await getDocs(q);
      console.log('[AdminDashboard] Review case count:', snap.size);
      const cases = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const vData = docSnap.data();
          let idImageUrl = null;
          let selfieUrl  = null;
          console.log(
            '[AdminDashboard] Fetching review assets for:',
            vData.userId,
            vData.idImagePath,
            vData.selfiePath
          );
          try {
            const imageResult = await getVerificationImages({ userId: vData.userId });
            console.log(
              '[AdminDashboard] Signed URL response for',
              vData.userId,
              imageResult?.data
            );
            idImageUrl = imageResult?.data?.idUrl || null;
            selfieUrl = imageResult?.data?.selfieUrl || null;
            console.log(
              '[AdminDashboard] Final image URLs for',
              vData.userId,
              { idImageUrl, selfieUrl }
            );
          } catch (imageErr) {
            console.error(`Admin dashboard image fetch failed for ${vData.userId}:`, imageErr);
          }

          return {
            ...vData,
            id: docSnap.id,
            idImageUrl,
            selfieUrl,
          };
        })
      );
      setIdReviewCases(cases);
    } catch (err) {
      console.error('Admin dashboard error:', err);
      setIdReviewCases([]);
      setIdReviewError(err.message || 'Failed to load review cases.');
    } finally {
      setIdReviewLoading(false);
    }
  };

  // Load review cases when that tab is first selected
  useEffect(() => {
    if (selectedTab === 'idReview') {
      fetchIdReviewCases();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const refreshHistoricalJob = async ({manageBusy = true} = {}) => {
    const requestedSourceId = historicalSourceIdRef.current;
    if (requestedSourceId === HARNETT_SOURCE_ID) return false;
    const requestId = ++historicalStatusRequest.current;
    if (manageBusy) setHistoricalBusy(true);
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({action: 'status', sourceId: requestedSourceId});
      if (requestId !== historicalStatusRequest.current) return;
      setHistoricalJob(result?.data?.job || null);
      const displayStatus = result?.data?.displayStatus;
      if (!displayStatus || displayStatus.updatedAt) {
        setHistoricalCorpusTotals(normalizeCorpusTotals(result?.data?.corpusTotals));
        setHistoricalGaps(normalizeAcquisitionGaps(result?.data?.acquisitionGaps, requestedSourceId));
      }
      // Review subtype counts are a separate authoritative summary. Keep this
      // refresh independent so a review read failure cannot hide acquisition status.
      try {
        const reviewSummary = await control({action: 'review_summary', sourceId: requestedSourceId});
        const summaryData = reviewSummary?.data || {};
        const hasSubtypeSummary = ['pendingReviewCount', 'nameReviewCount', 'duplicateReviewCount']
          .some((field) => Number.isFinite(summaryData[field]));
        if (hasSubtypeSummary) {
          const pending = Number.isFinite(summaryData.pendingReviewCount) ?
            safePendingReviewCount(summaryData.pendingReviewCount) :
            safePendingReviewCount(result?.data?.pendingReviewCount);
          const duplicate = Number.isFinite(summaryData.duplicateReviewCount) ?
            safePendingReviewCount(summaryData.duplicateReviewCount) : 0;
          const name = Number.isFinite(summaryData.nameReviewCount) ?
            safePendingReviewCount(summaryData.nameReviewCount) : Math.max(0, pending - duplicate);
          setReviewQueueCounts({duplicateReviewCount: duplicate, nameReviewCount: name,
            pendingReviewCount: pending, duplicateReviewState: {
              pending: safePendingReviewCount(summaryData.duplicateReviewState?.pending ?? duplicate),
              processing: safePendingReviewCount(summaryData.duplicateReviewState?.processing),
              processingFailed: safePendingReviewCount(summaryData.duplicateReviewState?.processingFailed),
            }});
        } else {
          const pending = safePendingReviewCount(result?.data?.pendingReviewCount);
          setReviewQueueCounts({duplicateReviewCount: 0, nameReviewCount: pending, pendingReviewCount: pending,
            duplicateReviewState: {pending: 0, processing: 0, processingFailed: 0}});
        }
      } catch (_) {
        // The existing total from status remains usable when subtype counts fail.
      }
      // The historical status callable reads the authoritative controller. Projection
      // freshness only describes display metrics and must not gate acquisition controls.
      setHistoricalStatusConfirmed(true);
      setHistoricalDashboardNeedsInitialization(displayStatus?.reason === 'needs_initialization');
      setHistoricalMessage('');
      return true;
    } catch (error) {
      if (requestId !== historicalStatusRequest.current ||
        requestedSourceId !== historicalSourceIdRef.current) return false;
      setHistoricalStatusConfirmed(false);
      setHistoricalMessage(publicRecordErrorMessage(error,
          'Could not load historical acquisition status.'));
      return false;
    } finally {
      if (manageBusy) setHistoricalBusy(false);
    }
  };

  const refreshPublicRecordStatus = async ({manageBusy = true} = {}) => {
    if (historicalSourceIdRef.current === HARNETT_SOURCE_ID) return false;
    if (publicRecordsRefreshInFlight.current) {
      publicRecordsRefreshPending.current = true;
      return false;
    }
    publicRecordsRefreshInFlight.current = true;
    if (manageBusy) {
      setHistoricalBusy(true);
      setPublicRecordFaceCatchUpBusy('refresh');
    }
    try {
      const results = await Promise.all([
        refreshHistoricalJob({manageBusy: false}),
        refreshPublicRecordFaceCatchUp({manageBusy: false}),
      ]);
      return results.every(Boolean);
    } finally {
      publicRecordsRefreshInFlight.current = false;
      if (manageBusy) {
        setHistoricalBusy(false);
        setPublicRecordFaceCatchUpBusy('');
      }
      if (publicRecordsRefreshPending.current) {
        publicRecordsRefreshPending.current = false;
        window.setTimeout(() => refreshPublicRecordStatus(), 0);
      }
    }
  };

  useEffect(() => {
    if (selectedTab === 'publicRecords') {
      setHistoricalStatusConfirmed(false);
      setPublicRecordFaceStatusConfirmed(false);
      if (!harnettSourceSelected) refreshPublicRecordStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, historicalSourceId]);

  useEffect(() => {
    if (!acquisitionCalendarOpen) return undefined;
    let current = true;
    const requestId = ++acquisitionCalendarRequest.current;
    setAcquisitionCalendarBusy(true);
    setAcquisitionCalendarError('');
    const control = httpsCallable(functions, 'publicRecordHistoricalControl');
    control({action: 'calendar_get', sourceId: historicalSourceId, year: acquisitionCalendarYear})
      .then((result) => {
        if (!current || requestId !== acquisitionCalendarRequest.current) return;
        setAcquisitionCalendarDates(Array.isArray(result?.data?.checkedDates) ? result.data.checkedDates : []);
      })
      .catch((error) => {
        if (!current || requestId !== acquisitionCalendarRequest.current) return;
        setAcquisitionCalendarDates([]);
        setAcquisitionCalendarError(publicRecordErrorMessage(error, 'Could not load the acquisition calendar.'));
      })
      .finally(() => {
        if (current && requestId === acquisitionCalendarRequest.current) setAcquisitionCalendarBusy(false);
      });
    return () => { current = false; };
  }, [acquisitionCalendarOpen, acquisitionCalendarYear, historicalSourceId]);

  const openAcquisitionCalendar = () => {
    setAcquisitionCalendarYear(currentCalendarYear());
    setAcquisitionCalendarDates([]);
    setAcquisitionCalendarError('');
    setAcquisitionCalendarOpen(true);
  };

  const toggleAcquisitionCalendarDate = async (date) => {
    if (acquisitionCalendarBusy) return;
    setAcquisitionCalendarBusy(true);
    setAcquisitionCalendarError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({action: 'calendar_toggle', sourceId: historicalSourceId, date});
      setAcquisitionCalendarDates(Array.isArray(result?.data?.checkedDates) ? result.data.checkedDates : []);
    } catch (error) {
      setAcquisitionCalendarError(publicRecordErrorMessage(error, 'Could not update the acquisition calendar.'));
    } finally {
      setAcquisitionCalendarBusy(false);
    }
  };

  const historicalAcquisitionActive = ACTIVE_ACQUISITION_STATUSES.has(historicalJob?.status);

  useEffect(() => {
    let listener = null;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive && selectedTab === 'publicRecords' && !harnettSourceSelected) {
        refreshPublicRecordStatus();
      }
    }).then((handle) => { listener = handle; }).catch(() => {});
    const onVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      if (visible && selectedTab === 'publicRecords' && !harnettSourceSelected) {
        refreshPublicRecordStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (listener) listener.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, historicalSourceId]);

  const historicalControl = async (action) => {
    const requestId = ++historicalStatusRequest.current;
    setHistoricalBusy(true);
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const payload = {action, sourceId: historicalSourceId};
      const result = await control(payload);
      if (requestId !== historicalStatusRequest.current) return;
      setHistoricalJob(result?.data?.job || null);
      setHistoricalCorpusTotals(normalizeCorpusTotals(result?.data?.corpusTotals));
      setHistoricalGaps(normalizeAcquisitionGaps(result?.data?.acquisitionGaps, historicalSourceId));
      setHistoricalStatusConfirmed(true);
      setHistoricalMessage('');
      await refreshPublicRecordStatus({manageBusy: false});
    } catch (error) {
      const actionMessage = publicRecordErrorMessage(error,
          'Historical acquisition action failed.');
      setHistoricalStatusConfirmed(false);
      if (callableErrorCode(error) === 'failed-precondition') {
        await refreshPublicRecordStatus({manageBusy: false});
      }
      setHistoricalMessage(actionMessage);
    } finally {
      setHistoricalBusy(false);
    }
  };

  const initializeHistoricalDashboardStatus = async () => {
    if (!historicalDashboardNeedsInitialization || historicalDashboardInitializeBusy) return;
    const sourceId = historicalSourceIdRef.current;
    setHistoricalDashboardInitializeBusy(true);
    setHistoricalDashboardInitializeError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      await control({action: 'dashboard_initialize', sourceId});
      const refreshed = await refreshPublicRecordStatus({manageBusy: false});
      if (!refreshed) {
        setHistoricalDashboardInitializeError(
            'Dashboard status initialization completed, but current status could not be confirmed. Refresh status before retrying.');
      }
    } catch (error) {
      setHistoricalDashboardInitializeError(publicRecordErrorMessage(error,
          'Could not initialize dashboard status.'));
    } finally {
      setHistoricalDashboardInitializeBusy(false);
    }
  };

  const loadTelemetry = async ({category = 'record_skip', reasonCode = '', cursor = '', append = false} = {}) => {
    if (!historicalJob || skipViewerBusy) return;
    setSkipViewerBusy(true);
    setSkipViewerError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({action: 'skip_events', sourceId: historicalSourceId,
        category, reasonCode, cursor});
      const next = result?.data || {};
      setSkipViewer((current) => ({...next, category,
        details: append ? [...(current?.details || []), ...(next.details || [])] : next.details || []}));
    } catch (error) {
      setSkipViewerError(error?.message || 'Could not load skipped-record telemetry.');
    } finally {
      setSkipViewerBusy(false);
    }
  };

  const openSkippedRecords = () => {
    setSkipViewer({category: 'record_skip', details: [], summary: []});
    loadTelemetry({category: 'record_skip'});
  };
  const openUnavailablePhotos = () => {
    setSkipViewer({category: 'photo_unavailable', details: [], summary: []});
    loadTelemetry({category: 'photo_unavailable'});
  };

  const loadRecordReviews = async (sourceId = reviewSourceFilter,
      {duplicateOnly = false, nameOnly = false, append = false, manageBusy = true, cursor = null} = {}) => {
    if (duplicateOnly && duplicateReviewRefillInFlight.current) return;
    if (nameOnly && append && nameReviewLoadMoreInFlight.current) return;
    if (duplicateOnly) duplicateReviewRefillInFlight.current = true;
    if (nameOnly && append) nameReviewLoadMoreInFlight.current = true;
    const requestGeneration = duplicateOnly ? ++duplicateReviewRequestGeneration.current : 0;
    const nameRequestGeneration = nameOnly ? ++nameReviewRequestGeneration.current : 0;
    if (manageBusy) setRecordReviewBusy(true);
    if (nameOnly && append) setNameReviewLoadMoreBusy(true);
    setRecordReviewError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({action: 'review_list', ...(sourceId ? {sourceId} : {}),
        ...(duplicateOnly ? {reviewType: 'DUPLICATE', limit: DUPLICATE_REVIEW_PAGE_LIMIT} : {}),
        ...(nameOnly ? {reviewType: 'NAME_MISMATCH', limit: NAME_REVIEW_PAGE_LIMIT, cursor} : {})});
      const candidates = (result?.data?.candidates || []).filter((candidate) =>
        (!duplicateOnly || (candidate.reviewType === 'DUPLICATE' && candidate.phase === 'PENDING_REVIEW' &&
          !duplicateReviewExcludedIds.current.has(candidate.reviewCandidateId))) &&
        (!nameOnly || candidate.reviewType !== 'DUPLICATE'));
      if (duplicateOnly && requestGeneration !== duplicateReviewRequestGeneration.current) return;
      if (nameOnly && nameRequestGeneration !== nameReviewRequestGeneration.current) return;
      if (append) {
        setRecordReviews((current) => {
          const existing = new Set(current.map((candidate) => candidate.reviewCandidateId));
          return [...current, ...candidates.filter((candidate) => !existing.has(candidate.reviewCandidateId))];
        });
      } else {
        setRecordReviews(candidates);
      }
      const pending = safePendingReviewCount(result?.data?.pendingReviewCount ?? result?.data?.pendingCount);
      setReviewQueueCounts({duplicateReviewCount: safePendingReviewCount(result?.data?.duplicateReviewCount),
        nameReviewCount: safePendingReviewCount(result?.data?.nameReviewCount ?? pending),
        pendingReviewCount: pending,
        duplicateReviewState: {
          pending: safePendingReviewCount(result?.data?.duplicateReviewState?.pending ?? result?.data?.duplicateReviewCount),
          processing: safePendingReviewCount(result?.data?.duplicateReviewState?.processing),
          processingFailed: safePendingReviewCount(result?.data?.duplicateReviewState?.processingFailed),
        }});
      if (nameOnly) setNameReviewNextCursor(result?.data?.nextCursor || null);
      if (!append) setRecordReviewSelections((current) => candidates.reduce((next, candidate) => ({...next,
        [candidate.reviewCandidateId]: candidate.decision === 'APPROVE_CARD_NAME' ?
          'APPROVE_CARD_NAME' : candidate.decision === 'APPROVE_REPORT_NAME' ?
            'APPROVE_REPORT_NAME' : current[candidate.reviewCandidateId]}), {}));
      if (!append) setRecordReviewEditing({});
      if (!append) setRecordReviewFailures({});
      if (sourceId === historicalSourceId) {
      }
    } catch (error) {
      setRecordReviewError(error?.message || 'Could not load pending record reviews.');
    } finally {
      if (duplicateOnly) duplicateReviewRefillInFlight.current = false;
      if (nameOnly && append) {
        nameReviewLoadMoreInFlight.current = false;
        setNameReviewLoadMoreBusy(false);
      }
      if (manageBusy) setRecordReviewBusy(false);
    }
  };
  const observationCleanupControl = () => httpsCallable(functions, 'publicRecordObservationCleanup');
  const refreshObservationCleanupStatus = async (sourceId = reviewSourceFilter, {updateMessage = true} = {}) => {
    if (sourceId !== 'nc_wake_bfs') {
      setObservationCleanupStatus(null);
      observationCleanupWasActionable.current = false;
      return null;
    }
    try {
      const result = await observationCleanupControl()({action: 'status', sourceId});
      const status = result?.data || null;
      const requiresDetails = cleanupNeedsDetails(status);
      if (requiresDetails) setObservationCleanupDetailsOpen(true);
      else if (observationCleanupWasActionable.current) setObservationCleanupDetailsOpen(false);
      observationCleanupWasActionable.current = requiresDetails;
      setObservationCleanupStatus(status);
      if (updateMessage) setObservationCleanupMessage(cleanupStatusMessage(status));
      return status;
    } catch (error) {
      setObservationCleanupMessage(error?.message || 'Could not load marked cleanup status.');
      return null;
    }
  };
  const duplicateBatchControl = () =>
    httpsCallable(functions, 'publicRecordDuplicateBatchControl');
  const refreshDuplicateBatchStatus = async (sourceId = reviewSourceFilter) => {
    if (sourceId !== 'nc_wake_bfs') {
      setDuplicateBatchStatus(null);
      return null;
    }
    try {
      const result = await duplicateBatchControl()({action: 'status', sourceId});
      const status = result?.data || null;
      setDuplicateBatchStatus(status);
      return status;
    } catch (error) {
      setDuplicateBatchError(publicRecordErrorMessage(error,
        'Could not load batch duplicate status.'));
      return null;
    }
  };
  const previewDuplicateBatch = async () => {
    setDuplicateBatchBusy('preview');
    setDuplicateBatchError('');
    setDuplicateBatchConfirmOpen(false);
    try {
      const result = await duplicateBatchControl()({
        action: 'preview',
        selection: duplicateBatchSelection === 'ALL' ? 'ALL' :
          Number(duplicateBatchSelection),
        sourceId: reviewSourceFilter,
      });
      setDuplicateBatchPreview(result?.data || null);
    } catch (error) {
      setDuplicateBatchPreview(null);
      setDuplicateBatchError(publicRecordErrorMessage(error,
        'Could not preview exact-name duplicate candidates.'));
    } finally {
      setDuplicateBatchBusy('');
    }
  };
  const startDuplicateBatch = async () => {
    if (!duplicateBatchPreview?.previewToken) return;
    setDuplicateBatchBusy('start');
    setDuplicateBatchError('');
    try {
      const result = await duplicateBatchControl()({
        action: 'start',
        previewToken: duplicateBatchPreview.previewToken,
        sourceId: reviewSourceFilter,
      });
      setDuplicateBatchStatus(result?.data || null);
      setDuplicateBatchPreview(null);
      setDuplicateBatchConfirmOpen(false);
    } catch (error) {
      setDuplicateBatchError(publicRecordErrorMessage(error,
        'Could not start batch duplicate resolution.'));
    } finally {
      setDuplicateBatchBusy('');
    }
  };
  const controlDuplicateBatch = async (action) => {
    setDuplicateBatchBusy(action);
    setDuplicateBatchError('');
    try {
      const result = await duplicateBatchControl()({
        action,
        sourceId: reviewSourceFilter,
      });
      setDuplicateBatchStatus(result?.data || null);
    } catch (error) {
      setDuplicateBatchError(publicRecordErrorMessage(error,
        `Could not ${action} batch duplicate resolution.`));
    } finally {
      setDuplicateBatchBusy('');
    }
  };
  useEffect(() => {
    if (selectedTab !== 'publicRecords') return undefined;
    refreshObservationCleanupStatus('nc_wake_bfs');
    const timer = window.setInterval(() => refreshObservationCleanupStatus('nc_wake_bfs'), 30000);
    return () => window.clearInterval(timer);
  // Cleanup execution is source-level and intentionally independent of the selected acquisition/workspace.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);
  useEffect(() => {
    setObservationCleanupDetailsOpen(false);
    observationCleanupWasActionable.current = false;
  }, [historicalSourceId]);
  const openReviewWorkspace = async (sourceId = '', queueView = 'name') => {
    if (queueView === 'duplicate' && duplicateReviewSourceRef.current !== sourceId) {
      duplicateReviewExcludedIds.current = new Set();
      duplicateReviewRequestGeneration.current += 1;
      duplicateReviewSourceRef.current = sourceId;
    }
    setReviewWorkspaceOpen(true);
    setReviewSourceFilter(sourceId);
    setReviewQueueView(queueView);
    setNameReviewNextCursor(null);
    await loadRecordReviews(sourceId, {duplicateOnly: queueView === 'duplicate', nameOnly: queueView === 'name'});
    if (queueView === 'duplicate') {
      await Promise.all([
        refreshObservationCleanupStatus(sourceId),
        refreshDuplicateBatchStatus(sourceId),
      ]);
    }
  };
  const loadMoreNameReviews = () => loadRecordReviews(reviewSourceFilter,
      {nameOnly: true, append: true, manageBusy: false, cursor: nameReviewNextCursor});
  useEffect(() => {
    if (!reviewWorkspaceOpen || reviewQueueView !== 'duplicate' || recordReviewBusy ||
        recordReviews.length === 0 || recordReviews.length > DUPLICATE_REVIEW_LOW_WATERMARK ||
        reviewQueueCounts.duplicateReviewCount <= recordReviews.length) return;
    loadRecordReviews(reviewSourceFilter, {duplicateOnly: true, append: true, manageBusy: false});
  // The refill is intentionally event-driven by local buffer consumption, not polling.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordReviews.length, reviewQueueCounts.duplicateReviewCount, reviewQueueView,
    reviewWorkspaceOpen, reviewSourceFilter, recordReviewBusy]);
  useEffect(() => {
    if (!reviewWorkspaceOpen || reviewQueueView !== 'duplicate' ||
        reviewSourceFilter !== 'nc_wake_bfs' ||
        !ACTIVE_DUPLICATE_BATCH_STATUSES.has(duplicateBatchStatus?.status)) {
      return undefined;
    }
    const timer = window.setInterval(async () => {
      const status = await refreshDuplicateBatchStatus(reviewSourceFilter);
      if (status?.status === 'COMPLETED') {
        await loadRecordReviews(reviewSourceFilter, {duplicateOnly: true});
      }
    }, 5000);
    return () => window.clearInterval(timer);
  // Batch status polling is active only while an explicitly started job runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateBatchStatus?.status, reviewQueueView,
    reviewSourceFilter, reviewWorkspaceOpen]);
  const adjustLocalReviewCount = (type) => {
    setReviewQueueCounts((current) => ({
      ...current,
      pendingReviewCount: Math.max(0, safePendingReviewCount(current.pendingReviewCount) - 1),
      [type === 'DUPLICATE' ? 'duplicateReviewCount' : 'nameReviewCount']:
        Math.max(0, safePendingReviewCount(current[type === 'DUPLICATE' ? 'duplicateReviewCount' : 'nameReviewCount']) - 1),
      duplicateReviewState: type === 'DUPLICATE' ? {...current.duplicateReviewState,
        pending: Math.max(0, safePendingReviewCount(current.duplicateReviewState?.pending) - 1),
        processing: safePendingReviewCount(current.duplicateReviewState?.processing) + 1} :
        current.duplicateReviewState,
    }));
  };
  const restoreLocalReviewCount = (type) => {
    setReviewQueueCounts((current) => ({
      ...current,
      pendingReviewCount: safePendingReviewCount(current.pendingReviewCount) + 1,
      [type === 'DUPLICATE' ? 'duplicateReviewCount' : 'nameReviewCount']:
        safePendingReviewCount(current[type === 'DUPLICATE' ? 'duplicateReviewCount' : 'nameReviewCount']) + 1,
      duplicateReviewState: type === 'DUPLICATE' ? {...current.duplicateReviewState,
        pending: safePendingReviewCount(current.duplicateReviewState?.pending) + 1,
        processing: Math.max(0, safePendingReviewCount(current.duplicateReviewState?.processing) - 1)} :
        current.duplicateReviewState,
    }));
  };
  const moveLocalReviewCountToDuplicate = () => {
    setReviewQueueCounts((current) => ({...current,
      duplicateReviewCount: safePendingReviewCount(current.duplicateReviewCount) + 1,
      nameReviewCount: Math.max(0, safePendingReviewCount(current.nameReviewCount) - 1),
      duplicateReviewState: {...current.duplicateReviewState,
        pending: safePendingReviewCount(current.duplicateReviewState?.pending) + 1},
    }));
  };
  const decideRecordReview = async (candidate, reject = false) => {
    const duplicate = candidate.reviewType === 'DUPLICATE';
    const selection = duplicate ? (reject ? 'NEW_PERSON' : 'DUPLICATE') :
      (reject ? 'REJECT' : recordReviewSelections[candidate.reviewCandidateId]);
    if (!selection) {
      setRecordReviewError('Select one of the two source-provided names before approving.');
      return;
    }
    if (recordReviewInFlight[candidate.reviewCandidateId]) return;
    const reviewId = candidate.reviewCandidateId;
    const previousIndex = recordReviews.findIndex((item) => item.reviewCandidateId === reviewId);
    setRecordReviewInFlight((current) => ({...current, [reviewId]: true}));
    setRecordReviewFailures((current) => ({...current, [reviewId]: ''}));
    if (duplicate) {
      duplicateReviewExcludedIds.current.add(reviewId);
      setRecordReviews((current) => current.filter((item) => item.reviewCandidateId !== reviewId));
      adjustLocalReviewCount('DUPLICATE');
    }
    setRecordReviewError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({action: 'review_decide', sourceId: candidate.sourceId,
        reviewCandidateId: reviewId, selection, ...(duplicate ? {queue: true} : {})});
      if (!duplicate) {
        if (result?.data?.convertedToDuplicate) {
          setRecordReviewError('The resolved Wake name has existing same-name candidates. Open Duplicate Reviews to decide it.');
          setRecordReviews((current) => current.filter((item) => item.reviewCandidateId !== reviewId));
          moveLocalReviewCountToDuplicate();
        } else {
          const selectedName = selection === 'APPROVE_CARD_NAME' ? candidate.cardName :
            selection === 'APPROVE_REPORT_NAME' ? candidate.reportName : '';
          const selectedNameSource = selection === 'APPROVE_CARD_NAME' ? 'CARD' :
            selection === 'APPROVE_REPORT_NAME' ? 'REPORT' : '';
          setRecordReviews((current) => current.map((item) => item.reviewCandidateId === reviewId ? {
            ...item, decision: selection, phase: 'DECIDED', selectedName, selectedNameSource,
          } : item));
          setRecordReviewEditing((current) => ({...current, [reviewId]: false}));
          adjustLocalReviewCount('NAME_MISMATCH');
        }
      }
    } catch (error) {
      const message = error?.message || 'Could not save the review decision.';
      setRecordReviewError(message);
      setRecordReviewFailures((current) => ({...current, [reviewId]: message}));
      if (duplicate) {
        duplicateReviewExcludedIds.current.delete(reviewId);
        setRecordReviews((current) => {
          const next = [...current];
          if (!next.some((item) => item.reviewCandidateId === reviewId)) {
            next.splice(Math.max(0, previousIndex), 0, candidate);
          }
          return next;
        });
        restoreLocalReviewCount('DUPLICATE');
      }
    } finally {
      setRecordReviewInFlight((current) => ({...current, [reviewId]: false}));
    }
  };
  const processRecordReviews = async () => {
    setRecordReviewBusy(true);
    setRecordReviewError('');
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      await control({action: 'review_process', ...(reviewSourceFilter ? {sourceId: reviewSourceFilter} : {})});
      await loadRecordReviews(reviewSourceFilter, {duplicateOnly: reviewQueueView === 'duplicate',
        nameOnly: reviewQueueView === 'name'});
    } catch (error) {
      setRecordReviewError(error?.message || 'Could not queue reviewed records.');
      setRecordReviewBusy(false);
    }
  };
  const markExistingObservationForCleanup = async (candidate, match) => {
    if (candidate.sourceId !== 'nc_wake_bfs' || observationCleanupBusy) return;
    setObservationCleanupBusy(`mark:${match.sourceRecordId}`);
    setObservationCleanupMessage('');
    try {
      const result = await observationCleanupControl()({action: 'mark', sourceId: candidate.sourceId,
        reviewCandidateId: candidate.reviewCandidateId, sourceRecordId: match.sourceRecordId});
      if (result?.data?.marked) {
        setObservationCleanupMessage(result.data.alreadyMarked ?
          'This existing observation was already marked for cleanup.' :
          'Existing observation marked for deferred cleanup.');
      }
      await refreshObservationCleanupStatus(candidate.sourceId, {updateMessage: false});
    } catch (error) {
      setObservationCleanupMessage(error?.message || 'Could not mark the existing observation for cleanup.');
    } finally {
      setObservationCleanupBusy('');
    }
  };
  const processMarkedObservationCleanup = async () => {
    const sourceId = 'nc_wake_bfs';
    if (sourceId !== 'nc_wake_bfs' || observationCleanupBusy) return;
    const count = observationCleanupStatus?.markedSourceRecordIds?.length || 0;
    if (!count) return;
    setObservationCleanupBusy('process');
    setObservationCleanupDetailsOpen(true);
    setObservationCleanupMessage('Marked cleanup is running in bounded background batches.');
    try {
      await observationCleanupControl()({action: 'start', sourceId});
      await Promise.all([
        refreshObservationCleanupStatus(sourceId),
        refreshPublicRecordStatus({manageBusy: false}),
      ]);
    } catch (error) {
      setObservationCleanupMessage(error?.message || 'Could not process marked cleanup.');
      await refreshObservationCleanupStatus(sourceId);
    } finally {
      setObservationCleanupBusy('');
    }
  };
  const pauseMarkedObservationCleanup = async () => {
    if (observationCleanupBusy) return;
    setObservationCleanupBusy('pause');
    setObservationCleanupMessage('');
    try {
      await observationCleanupControl()({action: 'pause', sourceId: 'nc_wake_bfs'});
      setObservationCleanupMessage('Cleanup will pause after the currently leased record or batch finishes.');
      await refreshObservationCleanupStatus('nc_wake_bfs');
    } catch (error) {
      setObservationCleanupMessage(error?.message || 'Could not pause marked cleanup.');
    } finally {
      setObservationCleanupBusy('');
    }
  };
  const reexamineRecordReview = (candidate) => {
    setRecordReviewEditing((current) => ({...current, [candidate.reviewCandidateId]: true}));
    if (['APPROVE_CARD_NAME', 'APPROVE_REPORT_NAME'].includes(candidate.decision)) {
      setRecordReviewSelections((current) => ({...current,
        [candidate.reviewCandidateId]: candidate.decision}));
    }
  };
  const reviewIsEditable = (candidate) => candidate.phase === 'PENDING_REVIEW' ||
    (candidate.phase === 'DECIDED' && recordReviewEditing[candidate.reviewCandidateId]);

  const archiveHistoricalJob = async () => {
    if (!historicalJob || historicalBusy) return;
    const range = historicalJob.historicalStartDate && historicalJob.historicalEndDate
      ? `${historicalJob.historicalStartDate} through ${historicalJob.historicalEndDate}`
      : 'this acquisition';
    if (!window.confirm(`Archive ${range} from Current Acquisition? This keeps its diagnostic history and does not delete records or retry work.`)) return;
    await historicalControl('archive');
  };

  const deferHistoricalPartition = async () => {
    if (!historicalJob || historicalBusy || historicalJob.canDeferPartitionGap !== true) return;
    const partition = historicalJob.currentDate || 'the current date';
    if (!window.confirm(`Defer ${partition} as an incomplete acquisition gap? The unresolved gap will be preserved for later recovery and acquisition will continue with the next requested date.`)) return;
    await historicalControl('defer_failed_partition');
  };

  const startNewHistoricalAcquisition = async (event) => {
    event.preventDefault();
    if (historicalBusy || historicalCreatePending.current || !historicalStatusConfirmed ||
      historicalAcquisitionActive) return;
    const { historicalStartDate, historicalEndDate } = historicalForm;
    if (!historicalStartDate || !historicalEndDate) {
      setHistoricalMessage('Choose both a start date and an end date for the new acquisition.');
      return;
    }
    const dateError = historicalDateRangeError(historicalStartDate, historicalEndDate);
    if (dateError) {
      setHistoricalMessage(dateError);
      return;
    }
    const controls = {
      processingBatchSize: Number(historicalForm.processingBatchSize),
      delayBetweenBatchesMs: Number(historicalForm.delayBetweenBatchesMs),
      maxConcurrentPhotos: Number(historicalForm.maxConcurrentPhotos),
    };
    const invalidControl = [
      ['Processing batch size', controls.processingBatchSize, 1, 100],
      ['Delay', controls.delayBetweenBatchesMs, 0, 3600000],
      ['Photo concurrency', controls.maxConcurrentPhotos, 1, 2],
    ].find(([, value, minimum, maximum]) => !Number.isInteger(value) || value < minimum || value > maximum);
    if (invalidControl) {
      const [label, , minimum, maximum] = invalidControl;
      setHistoricalMessage(`${label} must be a whole number from ${minimum} to ${maximum}.`);
      return;
    }
    const sourceLabel = HISTORICAL_SOURCES.find((source) => source.id === historicalSourceId)?.label || historicalSourceId;
    const confirmed = window.confirm(
        `Create a new historical acquisition for ${sourceLabel}, ${historicalStartDate} through ${historicalEndDate}?\n` +
        `Processing batch size: ${controls.processingBatchSize}\n` +
        `Delay (milliseconds): ${controls.delayBetweenBatchesMs}\n` +
        `Photo concurrency: ${controls.maxConcurrentPhotos}\n\n` +
        'This creates a new historical acquisition. It does not resume or retry the currently displayed acquisition.',
    );
    if (!confirmed) return;
    historicalCreatePending.current = true;
    const requestId = ++historicalStatusRequest.current;
    setHistoricalBusy(true);
    try {
      const control = httpsCallable(functions, 'publicRecordHistoricalControl');
      const result = await control({
        action: 'start',
        sourceId: historicalSourceId,
        historicalStartDate,
        historicalEndDate,
        controls,
      });
      if (requestId !== historicalStatusRequest.current) return;
      setHistoricalJob(result?.data?.job || null);
      setHistoricalCorpusTotals(normalizeCorpusTotals(result?.data?.corpusTotals));
      setHistoricalStatusConfirmed(true);
      setHistoricalMessage('');
      setNewAcquisitionOpen(false);
      await refreshPublicRecordStatus({manageBusy: false});
    } catch (error) {
      const startMessage = publicRecordErrorMessage(error,
          'Could not create the new historical acquisition.');
      const activeJobConflict = callableErrorCode(error) === 'failed-precondition' &&
        /active historical job|already running/i.test(error?.message || '');
      if (activeJobConflict) {
        await refreshPublicRecordStatus({manageBusy: false});
        setNewAcquisitionOpen(false);
        setHistoricalMessage(startMessage);
        return;
      }
      if (requestId === historicalStatusRequest.current) setHistoricalMessage(startMessage);
      setHistoricalStatusConfirmed(false);
      await refreshPublicRecordStatus({manageBusy: false});
      setHistoricalMessage(startMessage);
    } finally {
      historicalCreatePending.current = false;
      setHistoricalBusy(false);
    }
  };

  const handleIdReviewAction = async (userId, action) => {
    if (reviewActionLoading) return;
    const label = action === 'approved' ? 'Approve' : 'Reject';
    if (!window.confirm(`${label} this user's identity verification?`)) return;
    setReviewActionLoading(userId);
    try {
      const decideReview = httpsCallable(
        functions,
        'decideUserIdentityVerification'
      );
      await decideReview({ userId, decision: action });

      // Remove from local list
      setIdReviewCases((prev) => prev.filter((c) => c.userId !== userId));
      setExpandedReviewId(null);
    } catch (err) {
      console.error('handleIdReviewAction error:', err);
      alert('Action failed: ' + err.message);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const handleExpand = (id) => {
    setExpandedMessageId(expandedMessageId === id ? null : id);
    setReplyText('');
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;

    const reply = {
      from: 'admin',
      message: replyText,
      timestamp: new Date().toISOString(),
    };

    await addDoc(
        collection(firestore, `contactMessages/${messageId}/replies`),
        reply,
    );
    await updateDoc(
        doc(firestore, 'contactMessages', messageId),
        {status: 'responded'},
    );

    setReplyText('');
    setExpandedMessageId(null);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm(
        'Are you sure you want to delete this message? This cannot be undone.',
    )) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'contactMessages', messageId));
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const checkAdminClaims = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert('No signed-in user found.');
      return;
    }

    try {
      const token = await currentUser.getIdTokenResult(true);
      console.log('Admin token claims:', token.claims);
      alert(JSON.stringify(token.claims, null, 2));
    } catch (err) {
      console.error('Failed to load admin token claims:', err);
      alert('Failed to load admin token claims: ' + (err.message || err));
    }
  };

  const nameReviewCandidates = recordReviews.filter((candidate) => candidate.reviewType !== 'DUPLICATE');
  const duplicateReviewCandidates = recordReviews.filter((candidate) => candidate.reviewType === 'DUPLICATE' &&
    candidate.phase === 'PENDING_REVIEW');
  const currentDuplicateReview = duplicateReviewCandidates[0] || null;
  const markedObservationSourceRecordIds = new Set(observationCleanupStatus?.markedSourceRecordIds || []);
  const markedObservationCleanupCount = markedObservationSourceRecordIds.size;
  const duplicateCleanupBlocked = observationCleanupStatus?.duplicateState?.total > 0;
  const duplicateReviewState = reviewQueueCounts.duplicateReviewState ||
    {pending: reviewQueueCounts.duplicateReviewCount || 0, processing: 0, processingFailed: 0};
  const cleanupBacklog = (observationCleanupStatus?.requested || 0) +
    (observationCleanupStatus?.processing || 0) + (observationCleanupStatus?.retryable || 0) +
    (observationCleanupStatus?.deferred || 0);

  return (
    <div className="admin-dashboard">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{margin: 0}}>Admin Dashboard</h1>
        <button
          onClick={checkAdminClaims}
          style={{
            background: '#0f172a',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Check Admin Claims
        </button>
      </div>

      <div className="tabs">
        <button
          className={selectedTab === 'summary' ? 'active' : ''}
          onClick={() => setSelectedTab('summary')}
        >
          Summary
        </button>
        <button
          className={selectedTab === 'messages' ? 'active' : ''}
          onClick={() => setSelectedTab('messages')}
        >
          Messages
        </button>
        <button
          className={selectedTab === 'idReview' ? 'active' : ''}
          onClick={() => setSelectedTab('idReview')}
        >
          ID Review {idReviewCases.length > 0 && `(${idReviewCases.length})`}
        </button>
        <button
          className={selectedTab === 'publicRecords' ? 'active' : ''}
          onClick={() => setSelectedTab('publicRecords')}
        >
          Public Records Ingestion
        </button>
        <button
          className={selectedTab === 'publicRecordsCompliance' ? 'active compliance-tab' : 'compliance-tab'}
          onClick={() => setSelectedTab('publicRecordsCompliance')}
        >
          Compliance Removal
        </button>
      </div>

      {selectedTab === 'publicRecordsCompliance' && <PublicRecordComplianceRemoval />}

      {selectedTab === 'publicRecords' && (
        <section className="public-record-ingestion-panel">
          <div className="public-record-ingestion-heading">
            <div>
              <h2>Public Records Ingestion</h2>
            </div>
            {!harnettSourceSelected && <div className="admin-news-fetch-actions">
              <strong className={`ingestion-status ingestion-status-${(historicalJob?.status || 'IDLE').toLowerCase()}`}>
                {historicalJob?.status || 'IDLE'}
              </strong>
            </div>}
          </div>
          {!harnettSourceSelected && !historicalStatusConfirmed && <p className="ingestion-warning" role="status">
            Could not confirm acquisition status. Start New Acquisition is disabled until refresh succeeds.
          </p>}
          {!harnettSourceSelected && <section className="public-record-face-health" aria-labelledby="face-indexing-heading">
            <h3 id="face-indexing-heading">FACE INDEXING</h3>
            <p>Enroll retained Public Records photos into facial search.</p>
            <section className="public-record-face-catch-up" aria-labelledby="public-record-face-catch-up-heading">
              <h4 id="public-record-face-catch-up-heading">Face-Index Catch-up</h4>
              <p>Durable, source-scoped indexing. Closing this page does not stop an active job.</p>
              {publicRecordFaceCatchUp ? <>
                {publicRecordFaceCatchUp.status === 'COMPLETED' && <p role="status">
                  Catch-up complete. Corpus Totals is authoritative for current corpus counts.
                </p>}
                <dl aria-label="Selected source face-index catch-up status">
                  <div><dt>State</dt><dd>{publicRecordFaceCatchUp.status || 'IDLE'}</dd></div>
                  <div><dt>Retained Photos</dt><dd>{historicalCorpusTotals.retainedPhotos}</dd></div>
                  <div><dt>Indexed Faces</dt><dd>{historicalCorpusTotals.indexedFaces}</dd></div>
                  <div><dt>Queued</dt><dd>{publicRecordFaceCatchUp.work?.pending || 0}</dd></div>
                  <div><dt>Processing</dt><dd>{publicRecordFaceCatchUp.work?.leased || 0}</dd></div>
                  <div><dt>Failures / Terminal</dt><dd>{(publicRecordFaceCatchUp.counts?.terminalPhotoLocal || 0) +
                    (publicRecordFaceCatchUp.work?.permanentFailure || 0)}</dd></div>
                  <div><dt>Retryable work</dt><dd>{publicRecordFaceCatchUp.work?.retryableFailure || 0}</dd></div>
                  <div><dt>Remaining</dt><dd>{publicRecordFaceCatchUp.work?.remaining || 0}</dd></div>
                  <div><dt>Last Update</dt><dd>{formatHistoricalTimestamp(publicRecordFaceCatchUp.lastUpdatedAt)}</dd></div>
                </dl>
                <p className="ingestion-note">Scan pass {publicRecordFaceCatchUp.scanPass || 1} -
                  {' '}{publicRecordFaceCatchUp.counts?.eligible || 0} cumulative scan observations.</p>
                {publicRecordFaceCatchUp.lastErrorCode && <p className="ingestion-error">Error: {publicRecordFaceCatchUp.lastErrorCode}</p>}
              </> : <p>Refresh to load the durable selected-source catch-up job state.</p>}
              <div className="controlled-face-actions">
                {(!publicRecordFaceCatchUp || ['IDLE', 'COMPLETED'].includes(publicRecordFaceCatchUp.status)) &&
                <button type="button" disabled={Boolean(publicRecordFaceCatchUpBusy) ||
                  !publicRecordFaceStatusConfirmed}
                  onClick={() => setPublicRecordFaceCatchUpStartConfirmOpen(true)}>
                  {publicRecordFaceCatchUpBusy === 'start' ? 'Starting…' :
                    publicRecordFaceCatchUp?.status === 'COMPLETED' ? 'Start New Catch-up' :
                      'Start Catch-up'}
                </button>}
                {publicRecordFaceCatchUp?.status === 'SCANNING' || publicRecordFaceCatchUp?.status === 'DRAINING' ||
                  publicRecordFaceCatchUp?.status === 'RETRY_BACKOFF' ? <button type="button"
                    disabled={Boolean(publicRecordFaceCatchUpBusy) || !publicRecordFaceStatusConfirmed}
                    onClick={() => controlPublicRecordFaceCatchUp('catch_up_pause', 'pause')}>
                    {publicRecordFaceCatchUpBusy === 'pause' ? 'Pausing…' : 'Pause'}
                  </button> : null}
                {publicRecordFaceCatchUp?.status === 'PAUSED' ? <button type="button"
                  disabled={Boolean(publicRecordFaceCatchUpBusy) || !publicRecordFaceStatusConfirmed}
                  onClick={() => controlPublicRecordFaceCatchUp('catch_up_resume', 'resume')}>
                  {publicRecordFaceCatchUpBusy === 'resume' ? 'Resuming…' : 'Resume'}
                </button> : null}
                <button type="button" disabled={Boolean(publicRecordFaceCatchUpBusy)}
                  onClick={() => refreshPublicRecordStatus()}>
                  {publicRecordFaceCatchUpBusy === 'refresh' ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              {publicRecordFaceCatchUpStartConfirmOpen && <section className="public-record-face-confirmation"
                role="dialog" aria-modal="true" aria-labelledby="face-catch-up-start-confirmation-heading">
                <h5 id="face-catch-up-start-confirmation-heading">Start a new face-index catch-up scan?</h5>
                <p>This scans the selected source for retained Public Records photos that are not currently indexed.
                  Existing valid face indexes are not recreated unnecessarily.</p>
                <div className="controlled-face-actions">
                  <button type="button" disabled={Boolean(publicRecordFaceCatchUpBusy)}
                    onClick={() => setPublicRecordFaceCatchUpStartConfirmOpen(false)}>Cancel</button>
                  <button type="button" disabled={Boolean(publicRecordFaceCatchUpBusy) ||
                    !publicRecordFaceStatusConfirmed} onClick={confirmPublicRecordFaceCatchUpStart}>
                    Start Catch-up
                  </button>
                </div>
              </section>}
              {publicRecordFaceCatchUpError && <p className="ingestion-error" role="alert">
                Face-index catch-up failed: {publicRecordFaceCatchUpError}
              </p>}
            </section>
          </section>}
          {!harnettSourceSelected && <section className="historical-current-run" aria-labelledby="review-queue-heading">
            <h3 id="review-queue-heading">REVIEW QUEUE</h3>
            <div className="record-review-queue-counts">
              <button type="button" className={reviewQueueView === 'name' && reviewWorkspaceOpen ? 'active' : ''}
                onClick={() => openReviewWorkspace(historicalSourceId, 'name')} disabled={recordReviewBusy}>
                Name Reviews ({reviewQueueCounts.nameReviewCount || 0})
              </button>
              {HISTORICAL_SOURCES.find((source) => source.id === historicalSourceId)?.duplicateReviewEnabled &&
                <button type="button" className={reviewQueueView === 'duplicate' && reviewWorkspaceOpen ? 'active' : ''}
                  onClick={() => openReviewWorkspace(historicalSourceId, 'duplicate')} disabled={recordReviewBusy}>
                  Duplicate Reviews ({reviewQueueCounts.duplicateReviewCount || 0})
                </button>}
            </div>
          </section>}
          <section className="historical-current-run marked-cleanup-panel" aria-labelledby="cleanup-execution-heading">
            <div className="marked-cleanup-heading">
              <div><h3 id="cleanup-execution-heading">WAKE MARKED CLEANUP</h3>
                <p className="marked-cleanup-summary">{observationCleanupMessage ||
                  cleanupStatusMessage(observationCleanupStatus)}</p>
              </div>
              <div className="admin-news-fetch-actions marked-cleanup-actions">
                <button type="button" disabled={Boolean(observationCleanupBusy) ||
                  !observationCleanupStatus?.canStart || cleanupBacklog === 0}
                  onClick={processMarkedObservationCleanup}>
                  {observationCleanupBusy === 'process' ? 'Starting Cleanup…' :
                    `Start / Resume Marked Cleanup (${cleanupBacklog})`}
                </button>
                {observationCleanupStatus?.job?.enabled && <button type="button"
                  disabled={Boolean(observationCleanupBusy)} onClick={pauseMarkedObservationCleanup}>
                  {observationCleanupBusy === 'pause' ? 'Pausing Cleanup…' : 'Pause Cleanup'}
                </button>}
                <button type="button" className="marked-cleanup-details-toggle"
                  aria-expanded={observationCleanupDetailsOpen}
                  onClick={() => setObservationCleanupDetailsOpen((open) => !open)}>
                  {observationCleanupDetailsOpen ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
            </div>
            {!observationCleanupDetailsOpen && <p className="marked-cleanup-totals">
              Completed this run: {observationCleanupStatus?.currentRunCompleted || 0} · Retriable:{' '}
              {observationCleanupStatus?.retryable || 0} · Quarantined: {observationCleanupStatus?.quarantined || 0}
            </p>}
            {observationCleanupDetailsOpen && <>
              <dl className="historical-progress-grid marked-cleanup-progress-grid">
                <div><dt>Requested</dt><dd>{observationCleanupStatus?.requested || 0}</dd></div>
                <div><dt>Processing</dt><dd>{observationCleanupStatus?.processing || 0}</dd></div>
                <div><dt>Completed this run</dt><dd>{observationCleanupStatus?.currentRunCompleted || 0}</dd></div>
                <div><dt>Retryable</dt><dd>{observationCleanupStatus?.retryable || 0}</dd></div>
                <div><dt>Deferred for target work</dt><dd>{observationCleanupStatus?.deferred || 0}</dd></div>
                <div><dt>Quarantined</dt><dd>{observationCleanupStatus?.quarantined || 0}</dd></div>
              </dl>
              <p><strong>Worker:</strong> {cleanupWorkerStatus(observationCleanupStatus)}
                {observationCleanupStatus?.job?.lastWorkerActivityAt ?
                  ` · Last activity ${observationCleanupStatus.job.lastWorkerActivityAt}` : ''}</p>
              {observationCleanupStatus?.backlogByAcquisition?.length > 0 && <p>
                <strong>Backlog by acquisition:</strong>{' '}
                {observationCleanupStatus.backlogByAcquisition
                  .map((item) => `${item.runInstance}: ${item.count}`).join(' · ')}
              </p>}
              {duplicateCleanupBlocked && <p>Worker is waiting for all Wake Duplicate Reviews to finish.
                Pending: {observationCleanupStatus?.duplicateState?.pending || 0}; processing:{' '}
                {observationCleanupStatus?.duplicateState?.processing || 0}; failed:{' '}
                {observationCleanupStatus?.duplicateState?.processingFailed || 0}.</p>}
              {(observationCleanupStatus?.deferred || 0) > 0 && <p>
                {observationCleanupStatus.deferred} target{observationCleanupStatus.deferred === 1 ? ' is' : 's are'}
                {' '}temporarily deferred for active photo/face work; unrelated targets continue.</p>}
              {(observationCleanupStatus?.quarantined || 0) > 0 && <p className="ingestion-error">
                {observationCleanupStatus.quarantined} request{observationCleanupStatus.quarantined === 1 ? '' : 's'}
                {' '}quarantined after a permanent error or retry exhaustion.</p>}
              {observationCleanupStatus?.job?.lastErrorCode && <p className="ingestion-error">
                {observationCleanupStatus.job.lastErrorCode}: {observationCleanupStatus.job.lastErrorMessage}
              </p>}
              {observationCleanupStatus?.failureDetails?.length > 0 && <details>
                <summary>Cleanup request diagnostics ({observationCleanupStatus.failureDetails.length})</summary>
                <ul>{observationCleanupStatus.failureDetails.map((failure) => <li key={failure.cleanupRequestId}>
                  {failure.sourceRecordId?.slice(0, 16)}… · {failure.status} · {failure.lastErrorCode}
                  {failure.lastErrorMessage ? ` — ${failure.lastErrorMessage}` : ''}
                </li>)}</ul>
              </details>}
            </>}
          </section>
          <section className="record-acquisition" aria-labelledby="record-acquisition-heading">
            <h3 id="record-acquisition-heading">RECORD ACQUISITION</h3>
            <p>Acquire and process Public Records from the selected source.</p>
          {!newAcquisitionOpen && <div className="historical-source-calendar-actions">
            <label className="historical-source-select">Viewing source
                <select value={historicalSourceId}
                  onChange={(event) => selectAcquisitionSource(event.target.value)}>
                {ACQUISITION_SOURCES.map((source) => <option key={source.id} value={source.id}>{source.label}</option>)}
              </select>
            </label>
            {!harnettSourceSelected && <button type="button" className="acquisition-calendar-entry" onClick={openAcquisitionCalendar}
              disabled={historicalBusy} aria-haspopup="dialog">Calendar</button>
            }
          </div>}
          {harnettSourceSelected ? <HarnettAcquisitionPanel initiallyOpen /> : <>
          {historicalStatusConfirmed ? <PublicRecordCorpusTotals totals={historicalCorpusTotals} /> :
            <p className="ingestion-note" role="status">Loading source-specific statisticsâ€¦</p>}
          {historicalDashboardNeedsInitialization && <section className="historical-current-run"
            aria-labelledby="dashboard-status-initialization-heading">
            <h3 id="dashboard-status-initialization-heading">Dashboard Status</h3>
            <p>Corpus Totals require a one-time dashboard status initialization for this source. This performs a full corpus metrics scan.</p>
            <button type="button" className="new-historical-acquisition-button"
              disabled={historicalDashboardInitializeBusy}
              onClick={initializeHistoricalDashboardStatus}>
              {historicalDashboardInitializeBusy ? 'Initializing Dashboard Status…' : 'Initialize Dashboard Status'}
            </button>
            {historicalDashboardInitializeError && <p className="ingestion-error" role="alert">
              Dashboard status initialization failed: {historicalDashboardInitializeError}
            </p>}
          </section>}
          {historicalJob && historicalJob.status !== 'IDLE' && (
            <section className="historical-current-run" aria-labelledby="current-historical-run-heading">
              <h3 id="current-historical-run-heading">Existing / Current Acquisition</h3>
              <dl className="ingestion-metrics">
                <div><dt>Historical range</dt><dd>{historicalJob.historicalStartDate || 'N/A'} -
                  {' '}{historicalJob.historicalEndDate || 'N/A'}</dd></div>
                <div><dt>Current partition</dt><dd>{historicalJob.currentDate || 'N/A'}</dd></div>
                <div><dt>Raw records reported</dt><dd>{historicalJob.counters?.rawRowsDeclared || 0}</dd></div>
                <div><dt>Raw records retrieved</dt><dd>{historicalJob.counters?.rawRowsRetrieved || 0}</dd></div>
                <div><dt>Records normalized</dt><dd>{historicalJob.counters?.acceptedNormalizedRows || 0}</dd></div>
                {(historicalJob.counters?.skippedInvalidRows || 0) > 0 ?
                  <button type="button" className="ingestion-metric-button" onClick={openSkippedRecords}>
                    <dt>Records skipped</dt><dd>{historicalJob.counters.skippedInvalidRows}</dd>
                  </button> :
                  <div><dt>Records skipped</dt><dd>0</dd></div>}
                <div><dt>Records persisted</dt><dd>{historicalJob.counters?.recordsPersisted || 0}</dd></div>
                <div><dt>Duplicate records</dt><dd>{historicalJob.counters?.duplicateRows || 0}</dd></div>
                <div><dt>Current source identities</dt><dd>{historicalCorpusTotals.resolvedSourceIdentities || 0}</dd></div>
                <div><dt>Identity create events</dt><dd>{historicalJob.counters?.identitiesCreated || 0}</dd></div>
                <div><dt>Identity reuse events</dt><dd>{historicalJob.counters?.identitiesReused || 0}</dd></div>
                <div><dt>Photos attempted</dt><dd>{historicalJob.counters?.photoDownloadsAttempted || 0}</dd></div>
                <div><dt>Photos downloaded</dt><dd>{historicalJob.counters?.photoDownloadsSucceeded || 0}</dd></div>
                <div><dt>Photo download failures</dt><dd>{historicalJob.counters?.photoDownloadsFailed || 0}</dd></div>
                <div><dt>Photos rejected</dt><dd>{historicalJob.counters?.photosRejected || 0}</dd></div>
                {(historicalJob.counters?.photosUnavailable || 0) > 0 ?
                  <button type="button" className="ingestion-metric-button" onClick={openUnavailablePhotos}>
                    <dt>Photos unavailable</dt><dd>{historicalJob.counters.photosUnavailable}</dd>
                  </button> :
                  <div><dt>Photos unavailable</dt><dd>0</dd></div>}
                <div><dt>Retained selections</dt><dd>{historicalJob.counters?.photosRetained || 0}</dd></div>
                <button type="button" className="ingestion-metric-button"
                  onClick={() => openReviewWorkspace(historicalSourceId)}>
                  <dt>Pending Review</dt><dd>{historicalJob.counters?.pendingReview || 0}</dd>
                </button>
                <div><dt>Pending biometric work created</dt><dd>{historicalJob.counters?.pendingFaceWorkCreated || 0}</dd></div>
                <div><dt>Unresolved acquisition gaps</dt><dd>{historicalJob.gapCounters?.unresolvedGapCount || 0}</dd></div>
                <div><dt>Next retry</dt><dd>{historicalJob.status === 'RETRY_BACKOFF' ?
                  formatHistoricalTimestamp(historicalJob.nextAttemptAt) : 'N/A'}</dd></div>
                <div><dt>Last activity</dt><dd>{formatHistoricalTimestamp(historicalJob.lastActivityAt)}</dd></div>
              </dl>
              <p>Processing batch: {historicalJob.controls?.processingBatchSize || 20}; delay: {historicalJob.controls?.delayBetweenBatchesMs || 0} ms; photo workers: {historicalJob.controls?.maxConcurrentPhotos || 1}.</p>
              {wakeRetryMessage(historicalJob) &&
                <p className={currentErrorPresentationClass(historicalJob)}>{wakeRetryMessage(historicalJob)}</p>}
              {hasCurrentHistoricalError(historicalJob) &&
                <details data-testid="historical-current-error"
                  className={`ingestion-error-details ${currentErrorPresentationClass(historicalJob)}`}>
                  <summary>Technical error details</summary>
                  {historicalJob.lastErrorCode && <p>Error code: {historicalJob.lastErrorCode}</p>}
                  {historicalJob.lastErrorMessage && <p>
                    {historicalJob.status === 'ATTENTION_REQUIRED' ? 'Attention required: ' : ''}
                    {historicalJob.lastErrorMessage}
                  </p>}
                </details>}
              {(historicalJob.gapCounters?.unresolvedGapCount || 0) > 0 && historicalGaps.length > 0 && <details className="ingestion-error-details"
                data-testid="acquisition-gaps">
                <summary>ACQUISITION GAPS ({historicalJob.gapCounters.unresolvedGapCount})</summary>
                {historicalJob.historicalStartDate && historicalJob.historicalEndDate && <p>
                  Original range: {historicalJob.historicalStartDate} - {historicalJob.historicalEndDate}
                </p>}
                {historicalGaps.map((gap) => <article key={gap.gapId ||
                  `${gap.sourceId}-${gap.partition}-${gap.scope}-${gap.logicalPage || 'unknown'}`}
                className="historical-acquisition-gap">
                  <h4>{HISTORICAL_SOURCES.find((source) => source.id === gap.sourceId)?.label ||
                    gap.sourceId} - {gap.partition}</h4>
                  <dl className="ingestion-metrics">
                    <div><dt>Scope</dt><dd>{gapScopeLabel(gap.scope)}</dd></div>
                    {Number.isSafeInteger(gap.lastSafeLogicalPage) && <div>
                      <dt>Last safe page</dt><dd>{gap.lastSafeLogicalPage}</dd>
                    </div>}
                    {Number.isSafeInteger(gap.logicalPage) && <div>
                      <dt>Unresolved beginning</dt><dd>Page {gap.logicalPage}</dd>
                    </div>}
                    {Number.isSafeInteger(gap.cardPosition) && <div>
                      <dt>Card position</dt><dd>{gap.cardPosition}</dd>
                    </div>}
                    <div><dt>Failure code</dt><dd>{gap.failureCode || 'N/A'}</dd></div>
                    <div><dt>Reason</dt><dd>{gap.failureReason || 'N/A'}</dd></div>
                    <div><dt>Status</dt><dd>{gap.status}</dd></div>
                    <div><dt>Attempts</dt><dd>{gap.attemptCount}</dd></div>
                    <div><dt>First detected</dt><dd>{formatHistoricalTimestamp(gap.firstFailedAt)}</dd></div>
                    <div><dt>Last detected</dt><dd>{formatHistoricalTimestamp(gap.lastFailedAt)}</dd></div>
                  </dl>
                  <p>{gapScopeMeaning(gap.scope)}</p>
                </article>)}
              </details>}
              <div className="admin-news-fetch-actions">
                {['RUNNING', 'STARTING', 'RETRY_BACKOFF', 'PAUSE_REQUESTED'].includes(historicalJob.status) &&
                  <button type="button" disabled={historicalBusy} onClick={() => historicalControl('pause')}>Pause Existing Acquisition</button>}
                {historicalJob.status === 'PAUSED' &&
                  <button type="button" disabled={historicalBusy} onClick={() => historicalControl('resume')}>Resume Existing Acquisition</button>}
                {historicalJob.status === 'ATTENTION_REQUIRED' && historicalJob.canRetryFailedConnector === true &&
                  <button type="button" disabled={historicalBusy}
                    onClick={() => historicalControl('retry_failed_connector')}>
                    Retry Failed Connector &amp; Resume
                  </button>}
                {historicalJob.status === 'ATTENTION_REQUIRED' && historicalJob.canRecoverWakePagination === true && <>
                  <button type="button" disabled={historicalBusy}
                    onClick={() => historicalControl('recover_wake_pagination_checkpoint')}>
                    Replay Wake Checkpoint &amp; Resume
                  </button>
                  <p>A fresh Wake session will verify every completed page against its durable checkpoint before pagination continues.</p>
                </>}
                {historicalJob.status === 'ATTENTION_REQUIRED' && historicalJob.canDeferPartitionGap === true && <>
                  <button type="button" disabled={historicalBusy} onClick={deferHistoricalPartition}>
                    Defer {historicalJob.currentDate || 'Current Partition'} Gap &amp; Continue
                  </button>
                  <p>The affected date will remain incomplete. Its unresolved gap is preserved for later recovery while acquisition continues with the next requested date.</p>
                </>}
                {historicalJob.status === 'FAILED' &&
                  <button type="button" disabled={historicalBusy} onClick={() => historicalControl('resume')}>
                    Retry Current Acquisition ({historicalJob.historicalStartDate} to {historicalJob.historicalEndDate})
                  </button>}
                {['ATTENTION_REQUIRED', 'FAILED', 'PAUSED', 'COMPLETED', 'COMPLETED_WITH_GAPS'].includes(historicalJob.status) &&
                  <button type="button" disabled={historicalBusy} onClick={archiveHistoricalJob}>Archive Current Acquisition</button>}
                <button type="button" disabled={historicalBusy} onClick={refreshHistoricalJob}>Refresh</button>
              </div>
            </section>
          )}
          {reviewWorkspaceOpen && <section className="record-review-panel" aria-labelledby="pending-record-reviews-heading">
            <div className="record-review-panel-heading">
              <div><h3 id="pending-record-reviews-heading">Pending Record Reviews</h3>
               <p>Resolve source-name and likely-duplicate reviews independently. Decisions are processed durably.</p>
                </div>
              <div className="admin-news-fetch-actions">
                  <button type="button" disabled={recordReviewBusy}
                    onClick={async () => { await loadRecordReviews(reviewSourceFilter, {duplicateOnly: reviewQueueView === 'duplicate',
                      nameOnly: reviewQueueView === 'name'}); if (reviewQueueView === 'duplicate') await refreshObservationCleanupStatus(reviewSourceFilter); }}>Refresh Reviews</button>
                <button type="button" disabled={recordReviewBusy} onClick={processRecordReviews}>
                  Process Reviews{reviewSourceFilter ? ` (${HISTORICAL_SOURCES.find((source) => source.id === reviewSourceFilter)?.label || reviewSourceFilter})` : ' (All Sources)'}
                </button>
              </div>
            </div>
            <label className="historical-source-select">Review source
              <select value={reviewSourceFilter} disabled={recordReviewBusy}
                  onChange={(event) => { const sourceId = event.target.value; setReviewSourceFilter(sourceId); setObservationCleanupDetailsOpen(false);
                   if (reviewQueueView === 'duplicate') {
                     duplicateReviewExcludedIds.current = new Set();
                     duplicateReviewRequestGeneration.current += 1;
                     duplicateReviewSourceRef.current = sourceId;
                   }
                    setNameReviewNextCursor(null);
                    loadRecordReviews(sourceId, {duplicateOnly: reviewQueueView === 'duplicate',
                      nameOnly: reviewQueueView === 'name'});
                    if (reviewQueueView === 'duplicate') refreshObservationCleanupStatus(sourceId); }}>
                <option value="">All Sources</option>
                {HISTORICAL_SOURCES.map((source) => <option key={source.id} value={source.id}>{source.label}</option>)}
              </select>
            </label>
            {recordReviewError && <p className="ingestion-error">{recordReviewError}</p>}
            {recordReviewBusy && <p>Loading pending reviews…</p>}
            {!recordReviewBusy && recordReviews.length === 0 && reviewQueueView !== 'duplicate' &&
              <p>No review cards are currently available.</p>}
            {!recordReviewBusy && recordReviews.length === 0 && reviewQueueView === 'duplicate' && <p>
              No pending duplicate reviews. Processing: {duplicateReviewState.processing}; processing failed:{' '}
              {duplicateReviewState.processingFailed}.</p>}
             {reviewQueueView === 'name' && <div className="record-review-list">
              {nameReviewCandidates.map((candidate) => <article key={candidate.reviewCandidateId} className="record-review-card">
                {candidate.imageUrl && <img src={candidate.imageUrl} alt="Validated candidate mugshot" />}
                <div className="record-review-content">
                  <header><strong>{candidate.displayName}</strong><span>{candidate.phase}</span></header>
                  <dl><div><dt>Partition</dt><dd>{candidate.partitionDate || 'N/A'}</dd></div>
                    <div><dt>Page / card</dt><dd>{candidate.pagePosition ?? 'N/A'} / {candidate.cardPosition ?? 'N/A'}</dd></div>
                    <div><dt>Reason</dt><dd>{skipReasonLabel(candidate.reasonCode)}</dd></div></dl>
                  <p><strong>Name shown on Search Card:</strong><br />{candidate.cardName}</p>
                  <p><strong>Name shown on Detail Report:</strong><br />{candidate.reportName}</p>
                   {['DECIDED', 'PROCESSING', 'PROCESSING_FAILED'].includes(candidate.phase) && !reviewIsEditable(candidate) && <p className="record-review-decision">
                     {candidate.decision === 'REJECT' ? <>Decision: <strong>Reject</strong></> : <>
                       ✓ Selected for ingestion: <strong>{candidate.selectedName}</strong>
                       <small>{candidate.selectedNameSource === 'CARD' ? 'Search Card' : 'Detail Report'}</small>
                     </>}
                   </p>}
                   {reviewIsEditable(candidate) && <fieldset>
                    <legend>Select the name to use for this record:</legend>
                    {candidate.phase === 'DECIDED' && candidate.decision === 'REJECT' &&
                      <p>Current decision: Reject. Choose a name to replace it, or reject again.</p>}
                    <label><input type="radio" name={`review-name-${candidate.reviewCandidateId}`}
                      value="APPROVE_CARD_NAME"
                      checked={recordReviewSelections[candidate.reviewCandidateId] === 'APPROVE_CARD_NAME'}
                      onChange={(event) => setRecordReviewSelections((current) => ({...current,
                        [candidate.reviewCandidateId]: event.target.value}))} /> {candidate.cardName}
                      <small>Search Card</small></label>
                    <label><input type="radio" name={`review-name-${candidate.reviewCandidateId}`}
                      value="APPROVE_REPORT_NAME"
                      checked={recordReviewSelections[candidate.reviewCandidateId] === 'APPROVE_REPORT_NAME'}
                      onChange={(event) => setRecordReviewSelections((current) => ({...current,
                        [candidate.reviewCandidateId]: event.target.value}))} /> {candidate.reportName}
                      <small>Detail Report</small></label>
                  </fieldset>}
                  <div className="record-review-actions">
                    {candidate.officialSearchUrl && <a href={candidate.officialSearchUrl} target="_blank"
                      rel="noreferrer">Open Source Search</a>}
                     {reviewIsEditable(candidate) && <>
                        <button type="button" disabled={Boolean(recordReviewInFlight[candidate.reviewCandidateId])}
                          onClick={() => decideRecordReview(candidate)}>Approve</button>
                        <button type="button" className="record-review-reject" disabled={Boolean(recordReviewInFlight[candidate.reviewCandidateId])}
                          onClick={() => decideRecordReview(candidate, true)}>Reject</button></>}
                      {candidate.phase === 'PROCESSING_FAILED' && <button type="button"
                        className="record-review-reject" disabled={Boolean(recordReviewInFlight[candidate.reviewCandidateId])}
                        onClick={() => decideRecordReview(candidate, true)}>Reject</button>}
                      {candidate.phase === 'DECIDED' && !reviewIsEditable(candidate) && <button type="button"
                        disabled={Boolean(recordReviewInFlight[candidate.reviewCandidateId])} onClick={() => reexamineRecordReview(candidate)}>Re-examine</button>}
                   </div>
                   {recordReviewInFlight[candidate.reviewCandidateId] && <p>Decision saved locally; syncing…</p>}
                   {recordReviewFailures[candidate.reviewCandidateId] && <p className="ingestion-error">{recordReviewFailures[candidate.reviewCandidateId]}</p>}
                   {candidate.lastErrorCode && <p className="ingestion-error">{candidate.lastErrorCode}</p>}
                 </div>
                </article>)}
               {nameReviewNextCursor && <button type="button" className="load-more-name-reviews"
                 disabled={recordReviewBusy || nameReviewLoadMoreBusy} onClick={loadMoreNameReviews}>
                 {nameReviewLoadMoreBusy ? 'Loading Name Reviews…' : 'Load More Name Reviews'}
               </button>}
             </div>}
             {reviewQueueView === 'duplicate' && <section className="duplicate-review-panel" aria-labelledby="duplicate-review-heading">
               <header><h4 id="duplicate-review-heading">Duplicate Review</h4>
                 <span>Pending: {duplicateReviewState.pending} · Processing: {duplicateReviewState.processing} ·
                   Failed: {duplicateReviewState.processingFailed}</span></header>
                {reviewSourceFilter === 'nc_wake_bfs' && <section className="duplicate-batch-controls"
                  aria-labelledby="duplicate-batch-heading">
                  <div className="duplicate-batch-heading">
                    <div><h5 id="duplicate-batch-heading">Optional Exact-Name Batch</h5>
                      <p>Preview a stable range. Only nonempty first, middle, and last names that match
                        component-by-component are eligible. All other candidates stay pending.</p></div>
                    <strong>Never selects New Person</strong>
                  </div>
                  <div className="duplicate-batch-form">
                    <label>Candidates to examine
                      <select value={duplicateBatchSelection} disabled={Boolean(duplicateBatchBusy) ||
                        ACTIVE_DUPLICATE_BATCH_STATUSES.has(duplicateBatchStatus?.status)}
                        onChange={(event) => {
                          setDuplicateBatchSelection(event.target.value);
                          setDuplicateBatchPreview(null);
                          setDuplicateBatchConfirmOpen(false);
                        }}>
                        <option value="100">Next 100</option>
                        <option value="500">Next 500</option>
                        <option value="1000">Next 1,000</option>
                        <option value="ALL">All remaining</option>
                      </select>
                    </label>
                    <button type="button" disabled={Boolean(duplicateBatchBusy) ||
                      ACTIVE_DUPLICATE_BATCH_STATUSES.has(duplicateBatchStatus?.status)}
                      onClick={previewDuplicateBatch}>
                      {duplicateBatchBusy === 'preview' ? 'Previewing…' : 'Preview Exact Matches'}
                    </button>
                  </div>
                  <p className="duplicate-batch-normalization">Normalization uses Unicode NFKC,
                    case folding, and removal of punctuation and spacing. Missing middle names,
                    initials, truncations, and conflicts are excluded.</p>
                  {duplicateBatchPreview && <div className="duplicate-batch-preview" aria-live="polite">
                    <h6>Preview</h6>
                    <dl><div><dt>Examined</dt><dd>{duplicateBatchPreview.totalExamined}</dd></div>
                      <div><dt>Exact matches</dt><dd>{duplicateBatchPreview.exactMatches}</dd></div>
                      <div><dt>Excluded</dt><dd>{duplicateBatchPreview.excluded}</dd></div></dl>
                    {Object.keys(duplicateBatchPreview.excludedByReason || {}).length > 0 && <ul>
                      {Object.entries(duplicateBatchPreview.excludedByReason).map(([reason, count]) =>
                        <li key={reason}>{DUPLICATE_BATCH_REASON_LABELS[reason] || reason}: {count}</li>)}
                    </ul>}
                    <button type="button" disabled={Boolean(duplicateBatchBusy)}
                      onClick={() => setDuplicateBatchConfirmOpen(true)}>Review &amp; Confirm</button>
                  </div>}
                  {duplicateBatchConfirmOpen && duplicateBatchPreview && <div className="duplicate-batch-confirm"
                    role="dialog" aria-modal="false" aria-labelledby="duplicate-batch-confirm-heading">
                    <h6 id="duplicate-batch-confirm-heading">Confirm automatic Duplicate decisions</h6>
                    <p>This will queue {duplicateBatchPreview.exactMatches} exact-name candidate
                      {duplicateBatchPreview.exactMatches === 1 ? '' : 's'} through the existing
                      Duplicate pipeline. Excluded candidates remain pending for manual review.</p>
                    {historicalAcquisitionActive && <p className="ingestion-warning">Wake acquisition
                      is active. The batch cannot start until acquisition is idle.</p>}
                    <div><button type="button" className="secondary" disabled={Boolean(duplicateBatchBusy)}
                      onClick={() => setDuplicateBatchConfirmOpen(false)}>Cancel</button>
                      <button type="button" disabled={Boolean(duplicateBatchBusy) || historicalAcquisitionActive}
                        onClick={startDuplicateBatch}>Acknowledge &amp; Start Batch</button></div>
                  </div>}
                  {duplicateBatchStatus && <div className="duplicate-batch-progress" aria-live="polite">
                    <p><strong>Status: {duplicateBatchStatus.status?.replaceAll('_', ' ')}</strong></p>
                    <p>Processed {duplicateBatchStatus.nextIndex} of {duplicateBatchStatus.totalExamined} ·
                      Queued Duplicate: {duplicateBatchStatus.exactQueued} · Excluded:{' '}
                      {duplicateBatchStatus.excluded} · Concurrent manual decisions:{' '}
                      {duplicateBatchStatus.concurrentManual}</p>
                    <div>{ACTIVE_DUPLICATE_BATCH_STATUSES.has(duplicateBatchStatus.status) &&
                      <button type="button" className="secondary" disabled={Boolean(duplicateBatchBusy)}
                        onClick={() => controlDuplicateBatch('stop')}>Stop Batch</button>}
                      {(['STOPPED', 'FAILED'].includes(duplicateBatchStatus.status) ?
                        Boolean(duplicateBatchStatus.startedAt) :
                        duplicateBatchStatus.status === 'BLOCKED_ACQUISITION') &&
                        <button type="button" disabled={Boolean(duplicateBatchBusy) || historicalAcquisitionActive}
                          onClick={() => controlDuplicateBatch('resume')}>Resume Batch</button>}
                      <button type="button" className="secondary" disabled={Boolean(duplicateBatchBusy)}
                        onClick={() => refreshDuplicateBatchStatus(reviewSourceFilter)}>Refresh Progress</button>
                    </div>
                  </div>}
                  {duplicateBatchError && <p className="ingestion-error">{duplicateBatchError}</p>}
                </section>}
                {reviewSourceFilter === 'nc_wake_bfs' && markedObservationCleanupCount > 0 &&
                 <p>{markedObservationCleanupCount} existing record{markedObservationCleanupCount === 1 ? '' : 's'}
                   {' '}have persistent cleanup authorization. Worker controls are shown above.</p>}
              {!currentDuplicateReview && <p>No pending duplicate review for this source.</p>}
              {currentDuplicateReview && <article className="record-review-card duplicate-review-card">
                {currentDuplicateReview.imageUrl && <img src={currentDuplicateReview.imageUrl} alt="Incoming Wake mugshot" />}
                <div className="record-review-content">
                  <header><strong>{currentDuplicateReview.incomingName || currentDuplicateReview.reportName}</strong><span>INCOMING WAKE RECORD</span></header>
                  <p>Source Record Date: {currentDuplicateReview.incomingRecordDate || currentDuplicateReview.partitionDate || 'N/A'}</p>
                  {currentDuplicateReview.officialSearchUrl && <a href={currentDuplicateReview.officialSearchUrl} target="_blank" rel="noreferrer">Wake County Source</a>}
                  <h5>Possible Existing Matches</h5>
                  <div className="duplicate-review-candidates">
                    {(currentDuplicateReview.duplicateCandidates || []).map((match) => {
                      const markedForCleanup = markedObservationSourceRecordIds.has(match.sourceRecordId);
                      return <article key={match.sourceRecordId} className="duplicate-review-candidate"
                        aria-label={`Existing Wake candidate ${match.name || 'record'}`}>
                        {match.imageUrl && <img src={match.imageUrl} alt="Existing Wake candidate mugshot" />}
                        <strong>{match.name}</strong><span>Source Record Date: {match.sourceRecordDate || 'N/A'}</span>
                        <small>Middle: {match.middleRelationship?.replaceAll('_', ' ') || 'N/A'}</small>
                        {currentDuplicateReview.sourceId === 'nc_wake_bfs' && <button type="button"
                          className={`duplicate-review-candidate-cleanup${markedForCleanup ? ' is-marked' : ''}`}
                          disabled={Boolean(observationCleanupBusy)}
                          onClick={() => markExistingObservationForCleanup(currentDuplicateReview, match)}>
                          {markedForCleanup ? 'Marked for Cleanup' : 'Mark for Cleanup'}
                        </button>}
                      </article>;
                    })}
                  </div>
                  <div className="record-review-actions">
                    <button type="button" className="record-review-reject"
                      disabled={Boolean(recordReviewInFlight[currentDuplicateReview.reviewCandidateId])}
                      onClick={() => decideRecordReview(currentDuplicateReview)}>Duplicate</button>
                    <button type="button" disabled={Boolean(recordReviewInFlight[currentDuplicateReview.reviewCandidateId])}
                      onClick={() => decideRecordReview(currentDuplicateReview, true)}>New Person</button>
                  </div>
                  {recordReviewFailures[currentDuplicateReview.reviewCandidateId] && <p className="ingestion-error">{recordReviewFailures[currentDuplicateReview.reviewCandidateId]}</p>}
                </div>
              </article>}
            </section>}
          </section>}
          {skipViewer && <div className="skip-viewer-backdrop" role="presentation">
            <section className="skip-viewer" role="dialog" aria-modal="true" aria-labelledby="skip-viewer-heading">
              <div className="skip-viewer-heading">
                <div><h3 id="skip-viewer-heading">{skipViewer.category === 'photo_unavailable' ? 'Photos Unavailable' : 'Skipped Records'}</h3>
                  <p>{historicalSourceId} - {historicalJob?.historicalStartDate || 'N/A'} -
                    {' '}{historicalJob?.historicalEndDate || 'N/A'} - {skipViewer.totalSkipped ?? 0} total</p></div>
                <button type="button" onClick={() => setSkipViewer(null)} aria-label="Close telemetry viewer">X</button>
              </div>
              <div className="skip-reason-summary">
                <button type="button" className={!skipViewer.filterReasonCode ? 'active' : ''}
                  onClick={() => loadTelemetry({category: skipViewer.category, reasonCode: ''})}>
                  {skipViewer.category === 'photo_unavailable' ? 'All unavailable photos' : 'All skipped records'}</button>
                {(skipViewer.summary || []).map((reason) =>
                  <button type="button" key={reason.code}
                    className={skipViewer.filterReasonCode === reason.code ? 'active' : ''}
                    onClick={() => loadTelemetry({category: skipViewer.category, reasonCode: reason.code})}>
                    <span>{skipReasonLabel(reason.code)}</span><code>{reason.code}</code><strong>{reason.count}</strong>
                  </button>)}
              </div>
              {skipViewerError && <p className="ingestion-error">{skipViewerError}</p>}
              {!skipViewerBusy && !skipViewerError && (skipViewer.details || []).length === 0 &&
                <p>{skipViewer.category === 'photo_unavailable' ?
                  'No unavailable-photo outcomes were recorded for this acquisition.' :
                  'No skipped-record outcomes were recorded for this acquisition.'}</p>}
              <div className="skip-event-list">
                {(skipViewer.details || []).map((event) => <article key={event.eventId}>
                  <strong>{skipReasonLabel(event.reasonCode)}</strong><code>{event.reasonCode}</code>
                  <dl><div><dt>Stage</dt><dd>{event.stage || 'N/A'}</dd></div>
                    <div><dt>Observed</dt><dd>{formatHistoricalTimestamp(event.observedAt)}</dd></div>
                    <div><dt>Partition</dt><dd>{event.partitionDate || 'N/A'}</dd></div>
                    <div><dt>Page / card</dt><dd>{event.pagePosition || 'N/A'} / {event.cardPosition ?? 'N/A'}</dd></div>
                    <div><dt>Source record</dt><dd>{event.sourceRecordId || 'unavailable'}</dd></div>
                    <div><dt>Retryable</dt><dd>{event.retryable ? 'Yes' : 'No'}</dd></div></dl>
                </article>)}
              </div>
              {skipViewer.nextCursor && <button type="button" className="skip-load-more" disabled={skipViewerBusy}
                onClick={() => loadTelemetry({category: skipViewer.category, reasonCode: skipViewer.filterReasonCode,
                  cursor: skipViewer.nextCursor, append: true})}>Load more</button>}
              {skipViewerBusy && <p>Loading telemetry…</p>}
            </section>
          </div>}
          <section className="historical-new-acquisition" aria-labelledby="new-historical-acquisition-heading">
            <h3 id="new-historical-acquisition-heading">New Historical Acquisition</h3>
            <p>This creates a new historical acquisition. It does not resume or retry the currently displayed acquisition.</p>
            {!newAcquisitionOpen && <button type="button" className="new-historical-acquisition-button"
              disabled={historicalBusy || !historicalStatusConfirmed ||
                historicalAcquisitionActive}
              onClick={() => { setNewAcquisitionOpen(true); setHistoricalMessage(''); }}>
              Start New Acquisition
            </button>}
            {!newAcquisitionOpen && historicalAcquisitionActive &&
              <p className="ingestion-note">Only one active acquisition is permitted per source. Pause or resolve the current acquisition before starting a new one.</p>}
            {newAcquisitionOpen && <form className="ingestion-form" onSubmit={startNewHistoricalAcquisition}>
              <label>New acquisition source
              <select value={historicalSourceId} disabled={historicalBusy}
                onChange={(event) => { historicalStatusRequest.current += 1; setHistoricalSourceId(event.target.value); setHistoricalJob(null); setHistoricalStatusConfirmed(false); setHistoricalDashboardNeedsInitialization(false); setHistoricalDashboardInitializeError(''); setHistoricalMessage(''); }}>
                  {HISTORICAL_SOURCES.map((source) => <option key={source.id} value={source.id}>{source.label}</option>)}
                </select>
              </label>
              <label>Start date<input type="date" min="1000-01-01" max={sourceLocalDateKey()}
                value={historicalForm.historicalStartDate} disabled={historicalBusy}
                onChange={(event) => setHistoricalForm({...historicalForm, historicalStartDate: event.target.value})} /></label>
              <label>End date<input type="date" min="1000-01-01" max={sourceLocalDateKey()}
                value={historicalForm.historicalEndDate} disabled={historicalBusy}
                onChange={(event) => setHistoricalForm({...historicalForm, historicalEndDate: event.target.value})} /></label>
              <label>Processing batch size<input type="number" min="1" max="100" step="1" value={historicalForm.processingBatchSize} disabled={historicalBusy}
                onChange={(event) => setHistoricalForm({...historicalForm, processingBatchSize: event.target.value})} /></label>
              <label>Delay (milliseconds)<input type="number" min="0" max="3600000" step="1" value={historicalForm.delayBetweenBatchesMs} disabled={historicalBusy}
                onChange={(event) => setHistoricalForm({...historicalForm, delayBetweenBatchesMs: event.target.value})} /></label>
              <label>Photo concurrency<input type="number" min="1" max="2" step="1" value={historicalForm.maxConcurrentPhotos} disabled={historicalBusy}
                onChange={(event) => setHistoricalForm({...historicalForm, maxConcurrentPhotos: event.target.value})} /></label>
              <p className="ingestion-note">Defaults are 20 records per batch, 60,000 ms delay, and photo concurrency 1. The server validates all values.</p>
              <div className="admin-news-fetch-actions">
                <button type="submit" disabled={historicalBusy || !historicalStatusConfirmed ||
                  historicalAcquisitionActive}>Create New Historical Acquisition</button>
                <button type="button" disabled={historicalBusy} onClick={() => { setNewAcquisitionOpen(false); setHistoricalMessage(''); }}>Cancel</button>
              </div>
            </form>}
          </section>
          {(historicalJob?.runHistory || []).length > 0 && <section className="historical-run-history" aria-labelledby="historical-run-history-heading">
            <h3 id="historical-run-history-heading">Recent Runs</h3>
            <p className="ingestion-note">Archived and superseded runs are retained as read-only diagnostic history. Retry and resume apply only to the Current Acquisition.</p>
            <ul className="historical-run-history-list">
              {(historicalJob.runHistory || []).slice().sort((left, right) =>
                String(right.archivedAt || right.completedAt || right.updatedAt || '').localeCompare(
                    String(left.archivedAt || left.completedAt || left.updatedAt || ''))).slice(0, 6)
                  .map((run) => <li key={`${run.runInstance || 'historical'}-${run.archivedAt || run.historicalStartDate}`}>
                <strong>{run.status || 'ARCHIVED'}</strong>{' '}
                <span>{run.historicalStartDate || 'N/A'} - {run.historicalEndDate || 'N/A'}</span>
                 {Number.isSafeInteger(run.counters?.recordsPersisted) &&
                   <span> · {run.counters.recordsPersisted} records persisted</span>}
                {run.lastErrorCode && <span>; last error: {run.lastErrorCode}</span>}
              </li>)}
            </ul>
          </section>}
          {historicalMessage && <p className="ingestion-error">{historicalMessage}</p>}
          </>}
          </section>
          {acquisitionCalendarOpen && <div className="acquisition-calendar-backdrop" role="presentation">
            <section className="acquisition-calendar" role="dialog" aria-modal="true"
              aria-labelledby="acquisition-calendar-heading">
              <div className="acquisition-calendar-heading">
                <div>
                  <h3 id="acquisition-calendar-heading">Acquisition Coverage Calendar</h3>
                  <p>{HISTORICAL_SOURCES.find((source) => source.id === historicalSourceId)?.label || historicalSourceId}</p>
                </div>
                <button type="button" onClick={() => setAcquisitionCalendarOpen(false)}
                  aria-label="Close acquisition calendar">X</button>
              </div>
              <div className="acquisition-calendar-year-controls">
                <button type="button" disabled={acquisitionCalendarBusy}
                  onClick={() => setAcquisitionCalendarYear((year) => year - 1)} aria-label="Previous year">Previous Year</button>
                <strong aria-live="polite">{acquisitionCalendarYear}</strong>
                <button type="button" disabled={acquisitionCalendarBusy}
                  onClick={() => setAcquisitionCalendarYear((year) => year + 1)} aria-label="Next year">Next Year</button>
              </div>
              <p className="acquisition-calendar-legend">✓ Acquired (manual administrative tracking only)</p>
              {acquisitionCalendarError && <p className="ingestion-error" role="alert">{acquisitionCalendarError}</p>}
              <div className="acquisition-calendar-months" aria-busy={acquisitionCalendarBusy}>
                {CALENDAR_MONTHS.map((monthName, month) => {
                  const checkedDates = new Set(acquisitionCalendarDates);
                  const emptyDays = Array.from({length: calendarFirstWeekday(acquisitionCalendarYear, month)});
                  const days = Array.from({length: calendarDaysInMonth(acquisitionCalendarYear, month)}, (_, index) => index + 1);
                  return <section className="acquisition-calendar-month" key={monthName} aria-label={`${monthName} ${acquisitionCalendarYear}`}>
                    <h4>{monthName}</h4>
                    <div className="acquisition-calendar-weekdays" aria-hidden="true"><span>S</span><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span></div>
                    <div className="acquisition-calendar-days">
                      {emptyDays.map((_, index) => <span key={`empty-${index}`} />)}
                      {days.map((day) => {
                        const date = calendarDateKey(acquisitionCalendarYear, month, day);
                        const checked = checkedDates.has(date);
                        return <button type="button" key={date} disabled={acquisitionCalendarBusy}
                          className={checked ? 'acquisition-calendar-day checked' : 'acquisition-calendar-day'}
                          aria-pressed={checked} aria-label={`${monthName} ${day}, ${acquisitionCalendarYear}: ${checked ? 'acquired' : 'not acquired'}`}
                          onClick={() => toggleAcquisitionCalendarDate(date)}>{checked ? `✓ ${day}` : day}</button>;
                      })}
                    </div>
                  </section>;
                })}
              </div>
            </section>
          </div>}
        </section>
      )}

      {selectedTab === 'summary' && (
        <>
          <div className="dashboard-summary">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2>User Overview</h2>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                {loading && (
                  <span style={{fontSize: '0.9em', color: '#666'}}>
                    Refreshing stats...
                  </span>
                )}
                {rebuildingStats && (
                  <span style={{fontSize: '0.9em', color: '#c00'}}>
                    Rebuilding master stats...
                  </span>
                )}
                {rebuildingAgencies && (
                  <span style={{fontSize: '0.9em', color: '#c00'}}>
                    Rebuilding agencies by state...
                  </span>
                )}
                {rebuildingSearchLookups && (
                  <span style={{fontSize: '0.9em', color: '#c00'}}>
                    Rebuilding search lookups...
                  </span>
                )}
                <button
                  onClick={async () => {
                    setLoading(true);
                    await fetchSummaryData();
                    setLoading(false);
                  }}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                  disabled={loading}
                >
                  Rebuild Stats
                </button>
                <button
                  onClick={handleRebuildMasterStats}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    opacity: rebuildingStats ? 0.7 : 1,
                  }}
                  disabled={rebuildingStats}
                >
                  Full Rebuild (Admin)
                </button>
                <button
                  onClick={handleRebuildAgenciesByState}
                  style={{
                    backgroundColor: '#198754',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    opacity: rebuildingAgencies ? 0.7 : 1,
                  }}
                  disabled={rebuildingAgencies}
                >
                  Rebuild Agencies By State
                </button>
                <button
                  onClick={handleRebuildProfileSearchLookups}
                  style={{
                    backgroundColor: '#0f766e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    opacity: rebuildingSearchLookups ? 0.7 : 1,
                  }}
                  disabled={rebuildingSearchLookups}
                >
                  Rebuild Search Lookups
                </button>
              </div>
            </div>

            <div className="admin-news-fetch-panel">
              <div>
                <h3>News Fetch</h3>
                <p>Run the national or local news pipeline outside the schedule.</p>
              </div>
              <div className="admin-news-fetch-actions">
                <button
                  type="button"
                  onClick={() => handleRunNewsFetch('national')}
                  disabled={Boolean(newsFetchBusy)}
                >
                  {newsFetchBusy === 'national' ? 'Fetching...' : 'Fetch National'}
                </button>
                <button
                  type="button"
                  onClick={() => handleRunNewsFetch('local')}
                  disabled={Boolean(newsFetchBusy)}
                >
                  {newsFetchBusy === 'local' ? 'Fetching...' : 'Fetch Local'}
                </button>
              </div>
              {newsFetchMessage ? (
                <div className="admin-news-fetch-status">{newsFetchMessage}</div>
              ) : null}
            </div>

            {agencyMigrationSummary && (
              <p style={{fontSize: '0.9em', color: '#666'}}>
                Agencies by state rebuilt: {agencyMigrationSummary.statesUpdated ?? 0}
                {' '}states, {agencyMigrationSummary.totalAgencies ?? 0} agencies.
              </p>
            )}

            {searchLookupSummary && (
              <p style={{fontSize: '0.9em', color: '#666'}}>
                Search lookups rebuilt: {searchLookupSummary.statesUpdated ?? 0}
                {' '}states, {searchLookupSummary.totalAgencies ?? 0} agencies,
                {' '}{searchLookupSummary.totalRanks ?? 0} ranks,
                {' '}{searchLookupSummary.totalAssignments ?? 0} assignments.
              </p>
            )}

            {summaryData?.timestamp && (
              <p style={{fontSize: '0.9em', color: '#666'}}>
                Last updated:{' '}
                {new Date(summaryData.timestamp).toLocaleString()}
              </p>
            )}

            <ul>
              <li>
                Total Users:{' '}
                {summaryData?.byAgency
                  ? Object.values(summaryData.byAgency).reduce(
                      (sum, agency) => sum + (agency.total || 0),
                      0,
                    )
                  : '—'}
              </li>
              <li>
                Active: {summaryData?.byStatus?.active ?? '—'}
              </li>
              <li>
                Trial: {summaryData?.byStatus?.trial ?? '—'}
              </li>
              <li>
                Cancelled: {summaryData?.byStatus?.cancelled ?? '—'}
              </li>
              <li>
                Suspended: {summaryData?.byStatus?.suspended ?? '—'}
              </li>
            </ul>

            <div className="by-agency">
              <h3>By Agency</h3>
              {(summaryData?.byAgency
                ? Object.entries(summaryData.byAgency)
                : []
              ).map(([agency, data]) => (
                <div key={agency} className="agency-block">
                  <strong>
                    {agency} {data.total ?? '—'} users
                  </strong>
                  <ul>
                    {(data.byRank ? Object.entries(data.byRank) : []).map(
                        ([rank, count]) => (
                          <li key={rank}>
                            {rank}: {count ?? '—'}
                          </li>
                        ),
                    )}
                    {data.cancelled > 0 && (
                      <li>Cancelled: {data.cancelled}</li>
                    )}
                    {data.suspended > 0 && (
                      <li>Suspended: {data.suspended}</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {selectedTab === 'messages' && (
        <>
          <p>Review and respond to user-submitted contact messages below.</p>
          {messages.map((msg) => (
            <div key={msg.id} className="message-card">
              <div className="collapsed-view">
                <div
                  onClick={() => handleExpand(msg.id)}
                  style={{flex: 1, cursor: 'pointer'}}
                >
                  <strong>{msg.title || '(No Title)'}</strong> {' '}
                  {msg.message.slice(0, 50)}...
                  <div className="meta">
                    {msg.firstName} {msg.lastName} ({msg.rank},{' '}
                    {msg.assignment}, {msg.agency}) {' '}
                    {msg.timestamp?.toDate
                      ? msg.timestamp.toDate().toLocaleString()
                      : '(No Date)'}{' '}
                     Status: {msg.status}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(msg.id)}
                  title="Delete"
                >
                </button>
              </div>

              {expandedMessageId === msg.id && (
                <div className="expanded-view">
                  <p>{msg.message}</p>
                  {msg.replies?.length > 0 && (
                    <div className="admin-replies">
                      <strong>Your Reply:</strong>
                      {msg.replies.map((reply) => (
                        <div
                          key={reply.id}
                          style={{
                            marginBottom: '8px',
                            fontStyle: 'italic',
                            paddingLeft: '10px',
                            borderLeft: '2px solid #007bff',
                          }}
                        >
                          <p>{reply.message}</p>
                          <p
                            style={{
                              fontSize: '0.8em',
                              color: '#777',
                            }}
                          >
                            {reply.timestamp
                              ? new Date(reply.timestamp).toLocaleString()
                              : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    placeholder="Write your reply"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  ></textarea>
                  <button onClick={() => handleReply(msg.id)}>
                    Send Reply
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ── ID Review Tab ──────────────────────────────────────────── */}
      {selectedTab === 'idReview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
              Cases flagged for manual review. Approve or reject each submission.
            </p>
            <button
              onClick={fetchIdReviewCases}
              disabled={idReviewLoading}
              style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', opacity: idReviewLoading ? 0.6 : 1 }}
            >
              {idReviewLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {idReviewLoading && <p style={{ color: '#666', fontSize: '0.9em' }}>Loading review cases…</p>}

          {!idReviewLoading && idReviewError && (
            <p style={{ color: '#dc2626', fontSize: '0.9em' }}>
              Failed to load review cases: {idReviewError}
            </p>
          )}

          {!idReviewLoading && !idReviewError && idReviewCases.length === 0 && (
            <p style={{ color: '#666', fontSize: '0.9em' }}>No cases pending review. ✅</p>
          )}

          {idReviewCases.map((c) => {
            const name = [c.firstName, c.middleInitial, c.lastName].filter(Boolean).join(' ') || c.email || c.userId;
            const sim  = typeof c.faceSimilarity === 'number' ? c.faceSimilarity.toFixed(1) + '%' : '—';
            const simColor = c.faceSimilarity >= 90 ? '#16a34a' : c.faceSimilarity >= 80 ? '#ca8a04' : '#dc2626';
            const isExpanded = expandedReviewId === c.userId;
            const isActioning = reviewActionLoading === c.userId;
            const firstSubmitted = c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString() : '';
            const sentForReview = c.updatedAt?.toDate ? c.updatedAt.toDate().toLocaleString() : '';

            return (
              <div key={c.userId} className="message-card" style={{ marginBottom: 12 }}>
                {/* Collapsed header */}
                <div className="collapsed-view">
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpandedReviewId(isExpanded ? null : c.userId)}>
                    <strong>{name}</strong>
                    <span style={{ marginLeft: 8, fontSize: '0.82em', background: simColor, color: '#fff', borderRadius: 4, padding: '2px 7px' }}>
                      {sim}
                    </span>
                    <div className="meta">
                      {firstSubmitted ? 'First submitted: ' + firstSubmitted : 'First submitted: —'}
                      {sentForReview ? ' · Sent for review: ' + sentForReview : ''}
                    </div>
                    <div className="meta">
                      {c.rank} · {c.agency} · {c.email} · {c.phoneNumber}
                      {c.updatedAt?.toDate ? ' · ' + c.updatedAt.toDate().toLocaleDateString() : ''}
                      {' · Submissions: '}{c.submissionCount}
                    </div>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="expanded-view">
                    {/* Photos */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ margin: '0 0 4px', fontSize: '0.8em', fontWeight: 600 }}>Government ID</p>
                        {c.idImageUrl
                          ? <img src={c.idImageUrl} alt="ID" style={{ width: 200, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                          : <p style={{ fontSize: '0.8em', color: '#94a3b8', fontStyle: 'italic' }}>Images deleted after 72h</p>}
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px', fontSize: '0.8em', fontWeight: 600 }}>Selfie</p>
                        {c.selfieUrl
                          ? <img src={c.selfieUrl} alt="Selfie" style={{ width: 150, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                          : <p style={{ fontSize: '0.8em', color: '#94a3b8', fontStyle: 'italic' }}>Images deleted after 72h</p>}
                      </div>
                    </div>

                    {/* Scores */}
                    <p style={{ fontSize: '0.85em', margin: '0 0 6px' }}>
                      <strong>Face similarity:</strong>{' '}
                      <span style={{ color: simColor, fontWeight: 700 }}>{sim}</span>
                      {' · '}
                      <strong>Name match:</strong> {c.nameMatch ? '✅ Yes' : '❌ No'}
                      {' · '}
                      <strong>Strong name match:</strong> {c.strongNameMatch ? '✅ Yes' : '❌ No'}
                    </p>

                    <p style={{ fontSize: '0.85em', margin: '0 0 6px' }}>
                      <strong>Registered name:</strong> {name}
                      {' · '}
                      <strong>Email:</strong> {c.email || '—'}
                      {' · '}
                      <strong>Phone:</strong> {c.phoneNumber || '—'}
                    </p>

                    <p style={{ fontSize: '0.85em', margin: '0 0 10px' }}>
                      <strong>Agency:</strong> {c.agency || '—'}
                      {' · '}
                      <strong>Rank:</strong> {c.rank || '—'}
                    </p>

                    {/* Extracted text */}
                    {c.extractedText && (
                      <details style={{ marginBottom: 10 }}>
                        <summary style={{ fontSize: '0.83em', cursor: 'pointer', color: '#2563eb' }}>View extracted ID text</summary>
                        <pre style={{ fontSize: '0.78em', background: '#f8fafc', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6 }}>
                          {c.extractedText}
                        </pre>
                      </details>
                    )}
                    {!c.extractedText && (
                      <p style={{ fontSize: '0.8em', color: '#94a3b8', fontStyle: 'italic', marginBottom: 8 }}>Text data expired or unavailable.</p>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleIdReviewAction(c.userId, 'approved')}
                        disabled={isActioning}
                        style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, opacity: isActioning ? 0.6 : 1, minHeight: 44 }}
                      >
                        {isActioning ? 'Processing…' : '✅ Approve'}
                      </button>
                      <button
                        onClick={() => handleIdReviewAction(c.userId, 'rejected')}
                        disabled={isActioning}
                        style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, opacity: isActioning ? 0.6 : 1, minHeight: 44 }}
                      >
                        {isActioning ? 'Processing…' : '❌ Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
